(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./print-builder-logic.js'), require('./art-cards.js'));
  else root.PrintBuilderCore = factory(root.PrintBuilderLogic, root.ArtCards);
})(typeof self !== 'undefined' ? self : this, function (Logic, ArtCards) {
  const STATUS = Object.freeze({
    uploading: 'uploading',
    unchecked: 'unchecked',
    ready: 'ready',
    failed: 'failed',
    refused: 'refused'
  });
  const SHAPES = ['square', 'portrait', 'landscape'];
  const SWITCH_KEY = 'print-builder:switch';
  const STASH_KEY = 'print-builder:stash';
  const GUEST_KEY = 'print-builder:guest';
  const PACK_KEY = 'print-builder:pack';
  const STASH_MAX_AGE = 24 * 60 * 60 * 1000;
  const GUEST_MAX = 25;
  const TOAST_MS = 6000;
  const PACK_DELAY_MS = 400;
  const FIT_MODES = ['crop', 'fit', 'border'];
  const ORIENTATIONS = ['portrait', 'landscape'];
  const TURNS = [0, 90, 180, 270];
  const TABS = ['front', 'back'];
  const BACK_PARTS = ['sharedBack', 'sharedLogo', 'ownBack', 'ownLogo'];
  const MIDDLE = 0.5;
  const MOVES = { left: [-1, 0], right: [1, 0], up: [0, -1], down: [0, 1] };
  const KEYS = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
  const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const ADD = Object.freeze({ idle: 'idle', adding: 'adding', failed: 'failed', done: 'done' });
  const REASON_ORDER = Object.freeze(['no_quantity', 'no_variant', 'no_designs', 'too_many_designs', 'uploading', 'upload_failed', 'refused_file', 'unchecked', 'allocation']);
  const CART_CALLS = ['cut', 'upload', 'add', 'refresh'];
  const NOT_REFUSED = Object.freeze([401, 408, 429]);
  const CROP_KEYS = ['x', 'y', 'w', 'h'];
  const LOGO_FILES = { 'image/png': 'logo.png', 'image/jpeg': 'logo.jpg', 'image/svg+xml': 'logo.svg', 'image/webp': 'logo.webp' };
  const LOCKED_PARTS = ['quantity', 'upload'];
  const BUILDER_VERSION = 'v2';
  const EVENT_PARAMS = Object.freeze(['product', 'builder_version', 'quantity', 'change', 'value', 'variant_id', 'currency', 'source', 'file_type', 'design_index', 'ms', 'reason', 'dpi_band', 'step', 'designs_total', 'back_carried', 'where', 'packaging', 'code', 'mode', 'logged_in', 'size_band', 'from_product', 'to_product']);
  const VIEWS = ['photos', 'card'];
  const SIDES = ['front', 'back'];
  const SAVE = Object.freeze({ idle: 'idle', saving: 'saving' });
  const MIB = 1048576;
  const LARGE_FILE_MIB = Object.freeze([25, 50, 100]);
  const LOW_DPI_BAND = 100;
  const MAX_SIDE = 65535;
  const SENT_DETAIL_MIB = 25;
  const LIBRARY_ID = /^[1-9][0-9]{0,9}$/;
  const LIBRARY_ID_MAX = 2147483647;
  const LIBRARY_PARAMS = ['from', 'image', 'name', 'image_id', 'preview'];
  const CARD_ASPECTS = Object.freeze({ square: 1, landscape: 7 / 5, portrait: 5 / 7 });
  const FILE_TYPES = ['jpg', 'jpeg', 'png', 'webp'];
  const MIME_TYPES = Object.freeze({ 'image/jpeg': 'jpeg', 'image/png': 'png', 'image/webp': 'webp' });
  const FAIL_REASONS = ['size', 'type', 'network', 'server'];
  const DISCARDS = [STATUS.uploading, STATUS.unchecked, STATUS.failed];
  const MODE_EVENTS = Object.freeze({ crop: 'fill', fit: 'stretch', border: 'fit_white' });
  const PACKAGING_EVENTS = ['cards_only', 'sleeves'];
  const META_PREFIX = 'kf:';
  const CLARITY_TAGS = Object.freeze({
    size_switched: ['product', params => params.to_product],
    quantity_selected: ['qty', params => params.quantity],
    packaging_selected: ['packaging', params => params.packaging],
    error_shown: ['error', params => params.code],
    image_mode: ['mode', params => params.mode],
    rush_requested: ['rush', () => 'yes']
  });
  const instances = new Map();

  function fill(text, values){
    return String(text == null ? '' : text).replace(/\{(\w+)\}/g, (match, key) =>
      values && values[key] != null ? String(values[key]) : match);
  }

  function fileExtension(name){
    const match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return match ? match[1].toUpperCase() : '';
  }

  function httpsUrl(value){
    try {
      if(!/^https:[/][/]/i.test(String(value || ''))) return '';
      const url = new URL(String(value || ''));
      return url.protocol === 'https:' ? url.href : '';
    } catch(e){
      return '';
    }
  }

  function nameFromUrl(url){
    try {
      const segments = new URL(url).pathname.split('/').filter(Boolean);
      return segments.length ? decodeURIComponent(segments[segments.length - 1]) : '';
    } catch(e){
      return '';
    }
  }

  function findVariant(variants, quantity, packaging){
    if(!Array.isArray(variants) || !quantity) return null;
    return variants.find(v => v && Array.isArray(v.options) &&
      v.options[0] === String(quantity) && v.options[1] === packaging) || null;
  }

  function quantities(variants, packaging){
    const found = new Set();
    (Array.isArray(variants) ? variants : []).forEach(v => {
      if(!v || !Array.isArray(v.options) || v.options[1] !== packaging) return;
      const quantity = parseInt(v.options[0], 10);
      if(quantity > 0 && String(quantity) === v.options[0]) found.add(quantity);
    });
    return Array.from(found).sort((a, b) => a - b);
  }

  function serverUploadStub(design, onProgress){
    if(typeof onProgress === 'function') onProgress(100);
    return Promise.resolve({ previewUrl: design.previewUrl, ref: null });
  }

  function notWired(){
    return Promise.reject(new Error('not_wired'));
  }

  function cartOf(given){
    const source = given && typeof given === 'object' ? given : {};
    const cart = {};
    CART_CALLS.forEach(name => {
      cart[name] = typeof source[name] === 'function' ? source[name].bind(source) : notWired;
    });
    return cart;
  }

  function newSessionId(){
    return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  function dataUrlBlob(dataUrl){
    return fetch(dataUrl).then(response => response.blob());
  }

  function logoFileName(dataUrl){
    const match = /^data:([^;,]+)/i.exec(String(dataUrl || ''));
    return (match && LOGO_FILES[match[1].toLowerCase()]) || LOGO_FILES['image/png'];
  }

  function storedUrl(result){
    const url = result && result.url;
    if(typeof url !== 'string' || !url) throw new Error('no_url');
    return url;
  }

  function hasBackText(back){
    return !!back && !!(back.line1_text || back.line2_text || back.line3_text);
  }

  function drawable(back, logo){
    return hasBackText(back) || !!(logo && logo.dataUrl);
  }

  function renderInput(back, logo, orientation){
    return { backData: back, logoDataUrl: logo && logo.dataUrl, logoLayout: logo && logo.layout, logoPosition: logo && logo.position, orientation };
  }

  function productName(isSquare){
    return isSquare ? 'square' : '5x7';
  }

  function fileType(name, type){
    const match = /\.([^.]*)$/.exec(String(name || ''));
    const ext = match ? match[1].toLowerCase() : '';
    if(FILE_TYPES.includes(ext)) return ext;
    return MIME_TYPES[String(type || '').toLowerCase()] || '';
  }

  function sizeBand(bytes){
    const size = Number(bytes) || 0;
    if(size <= LARGE_FILE_MIB[0] * MIB) return '';
    for(let i = 1; i < LARGE_FILE_MIB.length; i += 1){
      if(size <= LARGE_FILE_MIB[i] * MIB) return LARGE_FILE_MIB[i - 1] + '-' + LARGE_FILE_MIB[i];
    }
    return LARGE_FILE_MIB[LARGE_FILE_MIB.length - 1] + '+';
  }

  function attempt(action){
    try {
      action();
    } catch(e){
      return;
    }
  }

  function sendEvent(win, prefix, name, params){
    const full = prefix + '_' + name;
    const data = Object.assign({}, params);
    attempt(() => {
      win.dataLayer = win.dataLayer || [];
      const flush = Object.fromEntries(EVENT_PARAMS.map(key => [key, undefined]));
      win.dataLayer.push(Object.assign({ event: full }, flush, data));
    });
    attempt(() => {
      const analytics = win.Shopify && win.Shopify.analytics;
      if(analytics && typeof analytics.publish === 'function') analytics.publish(META_PREFIX + full, data);
    });
    attempt(() => {
      if(typeof win.clarity !== 'function') return;
      win.clarity('event', name === 'editor_step' ? full + '_' + data.step : full);
      const tag = CLARITY_TAGS[name];
      if(tag) win.clarity('set', prefix + '_' + tag[0], String(tag[1](data)));
    });
  }

  function labelOf(d){
    return String(d.title || '').trim() || d.name;
  }

  function refusedBy(error){
    const status = error && error.status;
    return typeof status === 'number' && status >= 400 && status <= 499 && !NOT_REFUSED.includes(status);
  }

  function reasonOf(codes, quantity, problems, rules){
    const code = REASON_ORDER.find(name => (codes || []).includes(name));
    if(!code) return null;
    if(code === 'too_many_designs') return { code, problem: null, values: { allowed: Logic.maxDesigns(quantity, rules) } };
    if(code !== 'allocation') return { code, problem: null, values: {} };
    const list = problems || [];
    const sum = list.find(p => p.code === 'sum');
    if(sum) return { code, problem: 'sum', values: { quantity: sum.expected, assigned: sum.actual } };
    const below = list.find(p => p.code === 'below_min' || p.code === 'below_single_min');
    return below ? { code, problem: below.code, values: { min: below.min } } : { code, problem: null, values: {} };
  }

  function reasonText(reason, strings, format){
    if(!reason) return '';
    const values = Object.assign({}, reason.values);
    ['quantity', 'assigned'].forEach(key => { if(values[key] != null) values[key] = format.number(values[key]); });
    return fill((strings || {})['why_' + (reason.problem || reason.code)], values);
  }

  function summaryText(summary, strings, format){
    const t = (key, values) => fill((strings || {})[key], values);
    const none = t('sum_none');
    const designs = count => t(count === 1 ? 'sum_designs_one' : 'sum_designs_many', { count });
    const quantity = summary.quantity ? format.number(summary.quantity) : '';
    const why = reasonText(summary.reason, strings, format);
    const add = summary.add;
    const failed = add.failed;
    let sleeves = t('sum_sleeves_none');
    if(summary.sleeves.code === 'diff') sleeves = t('sum_sleeves_diff', { price: format.money(summary.sleeves.cents), quantity });
    else if(summary.sleeves.code === 'unavailable') sleeves = t('tile_unavailable');
    let each = '';
    if(!summary.quantity) each = t('sum_each_none');
    else if(summary.each != null) each = t('sum_each', { price: format.money(summary.each) }) + (summary.rush ? t('sum_each_rush') : '');
    return {
      size: summary.size,
      cards: quantity || none,
      designs: summary.designs ? designs(summary.designs) : none,
      packaging: t(summary.packaging === 'sleeves' ? 'sum_pack_sleeves' : 'sum_pack_cards'),
      sleeves,
      total: summary.total != null ? format.money(summary.total) : none,
      each,
      why,
      rows: add.rows.map(row => ({
        state: row.state,
        text: t(row.kind === 'front' ? 'add_row_front' : 'add_row_back', { name: row.name || t('library_name') }),
        sr: t('add_row_' + row.state)
      })),
      failedTitle: failed ? t(failed.kind === 'cart' ? 'add_cart_failed_title' : (failed.refused ? 'add_refused_title' : 'add_failed_title')) : '',
      failedText: failed ? t(failed.kind === 'cart' ? 'add_cart_failed_text' : (failed.refused ? 'add_refused_text' : 'add_failed_text'), { name: failed.name || t('library_name') }) : '',
      doneText: add.done ? t('add_done_text', {
        quantity: format.number(add.done.quantity),
        designs: designs(add.done.designs),
        rush: add.done.rush ? t('add_done_rush') : ''
      }) : '',
      barSub: !summary.quantity ? t('bar_none') : (why || t('bar_ready', { quantity, designs: designs(summary.designs) })),
      barLabel: t(summary.reason || add.status !== ADD.idle ? 'bar_continue' : 'bar_add')
    };
  }

  function clamp(value, min, max){
    return Math.min(Math.max(value, min), max);
  }

  function copyOf(value){
    return value && typeof value === 'object' && !Array.isArray(value) ? Object.assign({}, value) : null;
  }

  function editorSettings(raw){
    if(!raw || typeof raw !== 'object') return null;
    const zoomMin = Number(raw.zoomMin);
    const zoomMax = Number(raw.zoomMax);
    if(!(zoomMin > 0) || !(zoomMax >= zoomMin)) return null;
    return { zoomMin, zoomMax, stretchNote: Number(raw.stretchNote) || 0 };
  }

  function sizeLimitsOf(raw){
    const given = raw && typeof raw === 'object' ? raw : {};
    const positive = value => Number(value) > 0 ? Number(value) : null;
    return { maxBytes: positive(given.maxBytes), maxPixels: positive(given.maxPixels) };
  }

  function libraryId(value){
    if(value == null) return null;
    const text = String(value);
    if(!LIBRARY_ID.test(text)) return null;
    const id = Number(text);
    return id <= LIBRARY_ID_MAX ? id : null;
  }

  function printSize(width, height, rotation, mode, shape, crop){
    const turned = rotation === 90 || rotation === 270;
    const w = turned ? height : width;
    const h = turned ? width : height;
    const aspect = CARD_ASPECTS[shape] || CARD_ASPECTS.portrait;
    if(mode === 'crop'){
      const rect = crop || { x: 0, y: 0, w: 1, h: 1 };
      const left = Math.min(Math.round(rect.x * w), w - 1);
      const top = Math.min(Math.round(rect.y * h), h - 1);
      return {
        width: Math.max(1, Math.min(Math.round((rect.x + rect.w) * w), w) - left),
        height: Math.max(1, Math.min(Math.round((rect.y + rect.h) * h), h) - top)
      };
    }
    const wide = w / h > aspect;
    if(mode === 'fit') return { width: wide ? Math.round(h * aspect) : w, height: wide ? h : Math.round(w / aspect) };
    return { width: wide ? w : Math.round(h * aspect), height: wide ? Math.round(w / aspect) : h };
  }

  function openEditor(designId, instanceId){
    const list = instances.has(instanceId) ? [instances.get(instanceId)] : Array.from(instances.values());
    const found = list.find(instance => instance.builder.design(designId));
    if(found) found.openEditor(designId);
  }

  function createBuilder(options){
    const opts = options || {};
    const rules = opts.rules || ArtCards.RULES;
    const variants = Array.isArray(opts.variants) ? opts.variants : [];
    const size = ArtCards.sizeFor(opts.cardSize, opts.handle);
    const upload = typeof opts.upload === 'function' ? opts.upload : serverUploadStub;
    const preview = typeof opts.preview === 'function' ? opts.preview : () => '';
    const release = typeof opts.release === 'function' ? opts.release : () => {};
    const backEditor = opts.backEditor && typeof opts.backEditor === 'object' ? opts.backEditor : null;
    const storage = opts.storage || null;
    const saved = backEditor ? backEditor.loadSaved(storage, opts.backLimits) : null;
    const settings = editorSettings(opts.editor);
    const cart = cartOf(opts.cart);
    const renderPreview = typeof opts.renderBackPreview === 'function' ? opts.renderBackPreview : ArtCards.renderBackPreview;
    const renderPrint = typeof opts.renderBackPrint === 'function' ? opts.renderBackPrint : ArtCards.renderBackPrint;
    const toBlob = typeof opts.dataUrlToBlob === 'function' ? opts.dataUrlToBlob : dataUrlBlob;
    const customerId = opts.customerId || 'guest';
    const track = typeof opts.track === 'function' ? opts.track : () => {};
    const currency = typeof opts.currency === 'string' && opts.currency ? opts.currency : null;
    const now = typeof opts.now === 'function' ? opts.now : () => Date.now();
    const drafts = opts.drafts && typeof opts.drafts.create === 'function' && typeof opts.drafts.update === 'function' ? opts.drafts : null;
    const prepareRestored = typeof opts.prepareRestored === 'function' ? opts.prepareRestored : notWired;
    const productHandle = typeof opts.productHandle === 'string' && opts.productHandle ? opts.productHandle : (opts.handle == null ? '' : String(opts.handle));
    const productId = opts.productId == null ? null : opts.productId;
    const onGuestRef = typeof opts.onGuestRef === 'function' ? opts.onGuestRef : () => {};
    const sizeLimits = sizeLimitsOf(opts.limits);
    const prepareLibrary = typeof opts.prepareLibrary === 'function' ? opts.prepareLibrary : () => Promise.resolve({});
    const cancelUpload = typeof opts.cancelUpload === 'function' ? opts.cancelUpload : () => {};
    const product = productName(size.isSquare);
    const listeners = new Set();
    const state = {
      quantity: null,
      packaging: ArtCards.PACKAGING[0],
      designs: [],
      pending: null,
      editor: null,
      sharedBack: backEditor ? copyOf(saved && saved.back) || backEditor.defaults() : null,
      sharedLogo: copyOf(saved && saved.logo),
      rush: false,
      add: idleAdd(),
      preview: idlePreview(),
      save: { status: SAVE.idle }
    };
    let seq = 0;
    let prepared = null;
    let runs = 0;
    const started = new Map();
    const lowReported = new Set();
    const backImages = new Map();
    const preparing = new Set();
    const restoring = new Map();
    const logoUrls = new Map();
    const viewSessionId = newSessionId();
    let draftId = null;
    let quiet = false;
    let picked = false;
    let rushed = false;
    let shownReason = null;
    let shownStep = null;

    function emit(){
      notice();
      listeners.forEach(listener => listener(state));
    }

    function report(name, params){
      if(quiet) return;
      try {
        track(name, Object.assign({}, params, { product, builder_version: BUILDER_VERSION }));
      } catch(e){
        return;
      }
    }

    function lowShown(id, dpi){
      if(lowReported.has(id)) return;
      lowReported.add(id);
      report('low_res_shown', { dpi_band: '<' + (dpi < LOW_DPI_BAND ? LOW_DPI_BAND : ArtCards.QUALITY_MIN_DPI) });
    }

    function notice(){
      const shown = reason();
      const code = shown ? shown.code : null;
      if(code === 'no_variant' && shownReason !== code) report('error_shown', { where: 'summary', code });
      shownReason = code;
      const current = editing();
      const step = current ? JSON.stringify([current.d.id, state.editor.tab]) : null;
      if(step && step !== shownStep) report('editor_step', { step: state.editor.tab });
      shownStep = step;
      state.designs.forEach(d => {
        if(d.lowRes && (d.status === STATUS.unchecked || d.status === STATUS.ready)) lowShown(d.id, d.dpi);
      });
      if(current && state.editor.tab === TABS[0]){
        const dpi = cropDpi(current.d, current.draft);
        if(quality(dpi) === 'low') lowShown(current.d.id, dpi);
      }
    }

    function idlePreview(){
      return { id: null, view: VIEWS[0], side: SIDES[0] };
    }

    function idleAdd(){
      return { status: ADD.idle, steps: [], at: 0, failed: null, done: null };
    }

    function adding(){
      return state.add.status === ADD.adding;
    }

    function unDone(){
      if(state.add.status === ADD.done || state.add.status === ADD.failed) state.add = idleAdd();
    }

    function design(id){
      return state.designs.find(d => d.id === id) || null;
    }

    function live(){
      return state.designs.filter(d => d.status !== STATUS.refused);
    }

    function capacity(){
      return state.quantity ? Logic.maxDesigns(state.quantity, rules) : rules.maxDesigns;
    }

    function room(){
      return Math.max(0, capacity() - live().length);
    }

    function resplit(){
      const list = live();
      const allocs = state.quantity ? Logic.split(state.quantity, list.length, rules) : list.map(() => 0);
      list.forEach((d, index) => { d.alloc = allocs[index]; });
    }

    function makeDesign(fields){
      seq += 1;
      return Object.assign({
        id: 'd' + seq,
        name: '',
        title: '',
        type: '',
        bytes: 0,
        source: 'file',
        file: null,
        previewUrl: '',
        libraryUrl: '',
        uploadRef: null,
        status: STATUS.uploading,
        problem: null,
        progress: 0,
        alloc: undefined,
        width: 0,
        height: 0,
        dpi: 0,
        lowRes: false,
        orientation: 'portrait',
        ownBack: null,
        ownLogo: null,
        logoLayout: null
      }, fields);
    }

    function releasePreview(d){
      if(d.source === 'file' && d.previewUrl) release(d.previewUrl);
    }

    function usableRef(ref){
      if(!ref || typeof ref !== 'object') return null;
      if(ref.upload_id != null && ref.object_name != null) return { upload_id: ref.upload_id, object_name: ref.object_name };
      if(ref.library_image_id != null) return { library_image_id: ref.library_image_id };
      return null;
    }

    function guestRef(ref){
      if(customerId !== 'guest') return;
      try {
        onGuestRef(ref);
      } catch(e){
        return;
      }
    }

    function limitNotice(wanted, skipped){
      if(!skipped) return null;
      if(!state.quantity){
        return { quantity: null, skipped, allowed: rules.maxDesigns, cap: rules.maxDesigns, need: 0, overMax: true };
      }
      return Object.assign({ quantity: state.quantity, skipped }, Logic.designLimit(state.quantity, wanted, rules));
    }

    function onProgress(id, percent){
      const d = design(id);
      if(!d || d.status !== STATUS.uploading) return;
      d.progress = Math.max(0, Math.min(100, Math.floor(Number(percent) || 0)));
      emit();
    }

    function applySize(d, width, height){
      const w = Number(width) || 0;
      const h = Number(height) || 0;
      if(!w || !h) return false;
      const first = !d.width;
      d.width = w;
      d.height = h;
      d.dpi = Logic.printDpi({ width: w, height: h }, size);
      d.lowRes = Logic.isLowResolution(d.dpi, ArtCards.QUALITY_MIN_DPI);
      if(first) d.orientation = !size.isSquare && w > h ? 'landscape' : 'portrait';
      if(first && state.editor && state.editor.id === d.id) state.editor.draft.orientation = d.orientation;
      return true;
    }

    function succeed(d, ms){
      d.progress = 100;
      d.status = STATUS.unchecked;
      report('upload_succeeded', { design_index: live().indexOf(d) + 1, ms });
      emit();
    }

    function finishUpload(id, result){
      const d = design(id);
      if(!d || d.status !== STATUS.uploading) return;
      const next = result && result.previewUrl;
      if(next && next !== d.previewUrl){
        releasePreview(d);
        d.previewUrl = next;
      }
      d.uploadRef = result && result.ref != null ? result.ref : null;
      const ref = usableRef(d.uploadRef);
      if(ref && ref.upload_id != null) guestRef({ upload: ref });
      applySize(d, result && result.width, result && result.height);
      succeed(d, elapsed(id));
    }

    function finishPrepare(id, result){
      const d = design(id);
      if(!d || d.status !== STATUS.uploading) return;
      if(applySize(d, result && result.width, result && result.height)){
        const next = httpsUrl(result && result.previewUrl);
        if(next) d.previewUrl = next;
      } else {
        preparing.delete(id);
        d.previewUrl = d.libraryUrl;
      }
      succeed(d, 0);
    }

    function elapsed(id){
      const begun = started.has(id) ? started.get(id) : now();
      started.delete(id);
      return Math.max(0, Math.round(now() - begun));
    }

    function refuseUpload(d, problem, reason){
      d.status = STATUS.refused;
      d.problem = problem;
      d.progress = 0;
      started.delete(d.id);
      if(state.pending) state.pending.keep = state.pending.keep.filter(k => k !== d.id);
      resplit();
      report('upload_failed', { reason });
      report('error_shown', { where: 'upload', code: problem });
      emit();
    }

    function failUpload(id, error){
      const d = design(id);
      if(!d || d.status !== STATUS.uploading) return;
      const reason = error && FAIL_REASONS.includes(error.reason) ? error.reason : 'network';
      if(error && (error.refuse === 'too_large' || error.refuse === 'unsupported')){
        refuseUpload(d, error.refuse, reason);
        return;
      }
      d.status = STATUS.failed;
      started.delete(id);
      report('upload_failed', { reason });
      report('error_shown', { where: 'upload', code: 'upload_failed' });
      emit();
    }

    function startUpload(d){
      d.status = STATUS.uploading;
      d.progress = 0;
      started.set(d.id, now());
      let pending;
      try {
        pending = Promise.resolve(upload(d, percent => onProgress(d.id, percent)));
      } catch(e){
        pending = Promise.reject(e);
      }
      return pending.then(result => finishUpload(d.id, result), error => failUpload(d.id, error));
    }

    function startPrepare(d){
      d.status = STATUS.uploading;
      d.progress = 100;
      let pending;
      try {
        pending = Promise.resolve(prepareLibrary(d));
      } catch(e){
        pending = Promise.reject(e);
      }
      return pending.then(result => finishPrepare(d.id, result), error => failUpload(d.id, error));
    }

    function stopUpload(d){
      if(d.status !== STATUS.uploading) return;
      try {
        cancelUpload(d.id);
      } catch(e){
        return;
      }
    }

    function fileCheck(file){
      const bytes = Number(file && file.size) || 0;
      const problem = Logic.fileProblem({ type: file && file.type, name: file && file.name });
      if(problem) return { file, problem, reason: 'type' };
      if(bytes <= 0) return { file, problem: 'unsupported', reason: 'type' };
      if(sizeLimits.maxBytes && bytes > sizeLimits.maxBytes) return { file, problem: 'too_large', reason: 'size' };
      return { file, problem: null, reason: null };
    }

    function uploadStarted(d, source){
      report('upload_started', { source, file_type: fileType(d.name, d.type) });
      const band = sizeBand(d.bytes);
      if(band) report('upload_large', { size_band: band });
    }

    function addFiles(files, source){
      if(adding() || state.save.status === SAVE.saving) return { added: [], refused: [], skipped: 0, limit: null, done: Promise.resolve([]) };
      const from = source === 'drag' ? 'drag' : 'browse';
      const incoming = Array.from(files || []).map(fileCheck);
      const wanted = live().length + incoming.filter(item => !item.problem).length;
      let free = room();
      let skipped = 0;
      const added = [];
      const refused = [];
      incoming.forEach(({ file, problem, reason }) => {
        const base = {
          name: String((file && file.name) || ''),
          type: String((file && file.type) || ''),
          bytes: Number(file && file.size) || 0
        };
        if(problem){
          const d = makeDesign(Object.assign(base, { status: STATUS.refused, problem }));
          state.designs.push(d);
          refused.push(d);
          report('upload_failed', { reason });
          report('error_shown', { where: 'upload', code: problem });
          return;
        }
        if(free <= 0){
          skipped += 1;
          return;
        }
        free -= 1;
        let url = '';
        try {
          url = preview(file) || '';
        } catch(e){
          url = '';
        }
        const d = makeDesign(Object.assign(base, { file, previewUrl: url }));
        state.designs.push(d);
        added.push(d);
        uploadStarted(d, from);
      });
      resplit();
      const limit = limitNotice(wanted, skipped);
      if(skipped) report('error_shown', { where: 'upload', code: 'design_limit' });
      if(added.length || refused.length){
        unDone();
        emit();
      }
      const done = Promise.all(added.map(startUpload));
      return { added: added.map(d => d.id), refused: refused.map(d => d.id), skipped, limit, done };
    }

    function addLibrary(images){
      if(adding() || state.save.status === SAVE.saving) return { added: [], refused: [], skipped: 0, limit: null };
      const seen = new Set(state.designs.map(d => d.libraryUrl).filter(Boolean));
      const incoming = [];
      (Array.isArray(images) ? images : []).forEach(image => {
        const url = httpsUrl(image && image.url);
        if(!url || seen.has(url)) return;
        seen.add(url);
        const preview = httpsUrl(image && image.previewUrl);
        incoming.push({
          url,
          name: String((image && image.name) || '').trim() || nameFromUrl(url),
          id: libraryId(image && image.id),
          preview: preview && preview !== url ? preview : ''
        });
      });
      const wanted = live().length + incoming.length;
      let free = room();
      let skipped = 0;
      const added = [];
      incoming.forEach(image => {
        if(free <= 0){
          skipped += 1;
          return;
        }
        free -= 1;
        const d = makeDesign({
          name: image.name,
          source: 'library',
          libraryUrl: image.url,
          previewUrl: image.preview || image.url,
          uploadRef: image.id === null ? null : { library_image_id: image.id },
          status: image.preview ? STATUS.uploading : STATUS.unchecked,
          progress: 100
        });
        state.designs.push(d);
        added.push(d);
        uploadStarted(d, 'library');
        if(image.preview) preparing.add(d.id);
        else report('upload_succeeded', { design_index: live().indexOf(d) + 1, ms: 0 });
      });
      resplit();
      if(skipped) report('error_shown', { where: 'upload', code: 'design_limit' });
      if(added.length){
        unDone();
        emit();
      }
      added.forEach(d => { if(preparing.has(d.id)) startPrepare(d); });
      return { added: added.map(d => d.id), refused: [], skipped, limit: limitNotice(wanted, skipped) };
    }

    function remove(id){
      const d = design(id);
      if(!d || adding()) return false;
      const discarded = DISCARDS.includes(d.status);
      stopUpload(d);
      state.designs = state.designs.filter(x => x !== d);
      releasePreview(d);
      started.delete(id);
      preparing.delete(id);
      restoring.delete(id);
      if(state.pending) state.pending.keep = state.pending.keep.filter(k => k !== id);
      if(state.editor && state.editor.id === id) state.editor = null;
      resplit();
      unDone();
      if(discarded) report('design_discarded', { where: 'remove' });
      emit();
      return true;
    }

    function retry(id){
      const d = design(id);
      if(!d || d.status !== STATUS.failed || adding()) return Promise.resolve(false);
      if(restoring.has(d.id)){
        const again = startRestore(d);
        unDone();
        emit();
        return again.then(() => true);
      }
      if(preparing.has(d.id)){
        const redo = startPrepare(d);
        unDone();
        emit();
        return redo.then(() => true);
      }
      if(d.source === 'file' && d.file){
        releasePreview(d);
        let url = '';
        try {
          url = preview(d.file) || '';
        } catch(e){
          url = '';
        }
        d.previewUrl = url;
      }
      const done = startUpload(d);
      unDone();
      emit();
      return done.then(() => true);
    }

    function setDimensions(id, width, height){
      const d = design(id);
      if(!d || d.status === STATUS.refused || adding() || preparing.has(id)) return false;
      if(!applySize(d, width, height)) return false;
      if(d.file && d.status === STATUS.uploading && sizeLimits.maxPixels && d.width * d.height > sizeLimits.maxPixels){
        stopUpload(d);
        refuseUpload(d, 'too_large', 'size');
        return true;
      }
      emit();
      return true;
    }

    function setQuantity(value){
      const quantity = parseInt(value, 10);
      if(!(quantity > 0) || adding()) return { applied: false, choose: false };
      if(quantity === state.quantity && !state.pending) return { applied: true, choose: false };
      const allowed = Logic.maxDesigns(quantity, rules);
      const list = live();
      if(list.length > allowed){
        state.pending = { quantity, allowed, keep: list.slice(0, allowed).map(d => d.id) };
        emit();
        return { applied: false, choose: true };
      }
      const changed = quantity !== state.quantity;
      state.pending = null;
      state.quantity = quantity;
      resplit();
      unDone();
      if(changed) quantityChosen();
      emit();
      return { applied: true, choose: false };
    }

    function quantityChosen(){
      if(quiet) return;
      const chosen = variant();
      const params = { quantity: state.quantity, change: picked };
      if(chosen) Object.assign(params, { value: chosen.price / 100, variant_id: chosen.id });
      report('quantity_selected', Object.assign(params, { currency }));
      picked = true;
    }

    function toggleKeep(id, on){
      if(adding() || !state.pending || !live().some(d => d.id === id)) return false;
      const keep = state.pending.keep.filter(k => k !== id);
      if(on) keep.push(id);
      state.pending.keep = live().map(d => d.id).filter(k => keep.includes(k));
      emit();
      return true;
    }

    function canApplyKeep(){
      return !!state.pending && state.pending.keep.length >= 1 && state.pending.keep.length <= state.pending.allowed;
    }

    function applyKeep(){
      if(adding() || !canApplyKeep()) return false;
      const keep = new Set(state.pending.keep);
      const dropped = live().filter(d => !keep.has(d.id));
      state.designs = state.designs.filter(d => d.status === STATUS.refused || keep.has(d.id));
      dropped.forEach(stopUpload);
      dropped.forEach(releasePreview);
      state.quantity = state.pending.quantity;
      state.pending = null;
      resplit();
      unDone();
      quantityChosen();
      emit();
      return true;
    }

    function cancelKeep(){
      if(adding() || !state.pending) return false;
      state.pending = null;
      emit();
      return true;
    }

    function setAlloc(id, value){
      const d = design(id);
      if(adding() || !d || d.status === STATUS.refused || !state.quantity) return false;
      const cards = parseInt(value, 10);
      d.alloc = cards > 0 ? cards : 0;
      unDone();
      emit();
      return true;
    }

    function setPackaging(packaging){
      if(adding() || !ArtCards.PACKAGING.includes(packaging)) return false;
      if(packaging === state.packaging) return true;
      state.packaging = packaging;
      unDone();
      report('packaging_selected', { packaging: PACKAGING_EVENTS[ArtCards.PACKAGING.indexOf(packaging)] });
      emit();
      return true;
    }

    function setRush(on){
      if(adding()) return false;
      if(!!on === state.rush) return true;
      state.rush = !!on;
      unDone();
      if(state.rush && !rushed){
        rushed = true;
        report('rush_requested', {});
      }
      emit();
      return true;
    }

    function applySwitch(saved){
      if(adding() || !saved || typeof saved !== 'object') return false;
      const packaging = ArtCards.PACKAGING.includes(saved.packaging) ? saved.packaging : null;
      const quantity = Number.isInteger(saved.quantity) && saved.quantity > 0 &&
        findVariant(variants, saved.quantity, packaging || state.packaging) ? saved.quantity : null;
      quiet = true;
      try {
        if(packaging) setPackaging(packaging);
        if(quantity) setQuantity(quantity);
      } finally {
        quiet = false;
      }
      return !!(packaging || quantity);
    }

    function quantityOptions(){
      return quantities(variants, state.packaging)
        .map(quantity => ({ quantity, variant: findVariant(variants, quantity, state.packaging) }))
        .filter(option => option.variant);
    }

    function confirmDesign(id){
      const d = design(id);
      if(adding() || !d || d.status !== STATUS.unchecked) return false;
      const list = live();
      const params = { design_index: list.indexOf(d) + 1, designs_total: list.length, back_carried: backCarried(d) };
      d.status = STATUS.ready;
      unDone();
      report('design_confirmed', params);
      emit();
      return true;
    }

    function backCarried(d){
      if(firstDesign(d) === d || d.ownBack) return false;
      const back = state.sharedBack;
      return !!(back && (back.line1_text || back.line3_text)) || !!state.sharedLogo;
    }

    function cardAspect(orientation){
      const px = ArtCards.printPixelSize({ isSquare: size.isSquare, orientation, dpi: Math.min(...ArtCards.PRINT_DPI_STEPS) });
      return px.width / px.height;
    }

    function geometry(d, draft){
      if(!d.width || !d.height) return null;
      return {
        imageWidth: d.width,
        imageHeight: d.height,
        rotation: draft.rotation,
        aspect: cardAspect(draft.orientation),
        zoom: draft.zoom,
        centerX: draft.centerX,
        centerY: draft.centerY
      };
    }

    function settle(d, draft){
      const g = geometry(d, draft);
      if(g) Object.assign(draft, Logic.centerOf(Logic.cropRect(g)));
    }

    function centre(draft){
      draft.centerX = MIDDLE;
      draft.centerY = MIDDLE;
    }

    function storedPlace(d, draft){
      if([d.zoom, d.centerX, d.centerY].every(Number.isFinite)){
        return { zoom: clamp(d.zoom, settings.zoomMin, settings.zoomMax), centerX: d.centerX, centerY: d.centerY };
      }
      const g = geometry(d, draft);
      if(g && draft.fitMode === 'crop' && d.crop && d.crop.w > 0 && d.crop.h > 0){
        return Object.assign({ zoom: clamp(Logic.zoomOf(d.crop, g), settings.zoomMin, settings.zoomMax) }, Logic.centerOf(d.crop));
      }
      return { zoom: settings.zoomMin, centerX: MIDDLE, centerY: MIDDLE };
    }

    function draftOf(d){
      const draft = {
        fitMode: FIT_MODES.includes(d.fitMode) ? d.fitMode : 'crop',
        rotation: TURNS.includes(d.rotation) ? d.rotation : TURNS[0],
        orientation: !size.isSquare && d.orientation === 'landscape' ? 'landscape' : 'portrait',
        title: String(d.title || ''),
        sharedBack: copyOf(state.sharedBack),
        sharedLogo: copyOf(state.sharedLogo),
        ownBack: copyOf(d.ownBack),
        ownLogo: copyOf(d.ownLogo)
      };
      return Object.assign(draft, storedPlace(d, draft));
    }

    function editorFor(d){
      return { id: d.id, tab: TABS[0], busy: false, draft: draftOf(d) };
    }

    function nextUnchecked(id){
      return state.designs.find(d => d.status === STATUS.unchecked && d.id !== id) || null;
    }

    function openDesignEditor(id){
      const d = design(id);
      if(adding() || !settings || !d || (d.status !== STATUS.unchecked && d.status !== STATUS.ready)) return false;
      state.editor = editorFor(d);
      emit();
      return true;
    }

    function editing(){
      const d = state.editor ? design(state.editor.id) : null;
      return d ? { d, draft: state.editor.draft } : null;
    }

    function cropDpi(d, draft){
      const g = geometry(d, draft);
      return g ? Logic.printDpi(Logic.coverPixels(draft.fitMode, g), size) : 0;
    }

    function quality(dpi){
      if(!dpi) return '';
      if(Logic.isLowResolution(dpi, ArtCards.QUALITY_MIN_DPI)) return 'low';
      return dpi >= Math.min(...ArtCards.PRINT_DPI_STEPS) ? 'sharp' : 'good';
    }

    function tooLargeFor(d, draft){
      const g = geometry(d, draft);
      if(!g) return false;
      const shape = size.isSquare ? SHAPES[0] : draft.orientation;
      const px = printSize(d.width, d.height, draft.rotation, draft.fitMode, shape, Logic.cropForMode(draft.fitMode, g));
      if(px.width > MAX_SIDE || px.height > MAX_SIDE) return true;
      return !!sizeLimits.maxPixels && px.width * px.height > sizeLimits.maxPixels;
    }

    function editorView(){
      const current = editing();
      if(!current) return null;
      const { d, draft } = current;
      const g = geometry(d, draft);
      const list = live();
      const next = nextUnchecked(d.id);
      const dpi = cropDpi(d, draft);
      const stretch = g ? Logic.stretchPercent(g) : 0;
      const tooLarge = tooLargeFor(d, draft);
      return {
        id: d.id,
        name: d.name,
        previewUrl: d.previewUrl,
        tab: state.editor.tab,
        number: list.indexOf(d) + 1,
        count: list.length,
        fitMode: draft.fitMode,
        rotation: draft.rotation,
        orientation: draft.orientation,
        zoom: draft.zoom,
        centerX: draft.centerX,
        centerY: draft.centerY,
        title: draft.title,
        zoomMin: settings.zoomMin,
        zoomMax: settings.zoomMax,
        crop: g ? Logic.cropForMode(draft.fitMode, g) : null,
        placement: g ? Logic.stagePlacement(draft.fitMode, g) : null,
        dpi,
        quality: quality(dpi),
        stretch,
        stretchShown: !!g && stretch >= settings.stretchNote,
        canMove: draft.fitMode === 'crop' && !!g,
        showOrientation: !size.isSquare,
        next: next ? next.id : null,
        busy: !!state.editor.busy,
        tooLarge,
        canConfirm: !!g && !state.editor.busy && !tooLarge
      };
    }

    function setEditorTab(tab){
      const current = editing();
      if(!current || !TABS.includes(tab)) return false;
      if(tab === TABS[1] && tooLargeFor(current.d, current.draft)) return false;
      state.editor.tab = tab;
      emit();
      return true;
    }

    function setFitMode(mode){
      const current = editing();
      if(!current || !FIT_MODES.includes(mode)) return false;
      const changed = mode !== current.draft.fitMode;
      current.draft.fitMode = mode;
      if(mode !== 'crop') centre(current.draft);
      if(changed) report('image_mode', { mode: MODE_EVENTS[mode] });
      emit();
      return true;
    }

    function setOrientation(orientation){
      const current = editing();
      if(!current || size.isSquare || !ORIENTATIONS.includes(orientation)) return false;
      current.draft.orientation = orientation;
      centre(current.draft);
      emit();
      return true;
    }

    function setZoom(value){
      const current = editing();
      const zoom = Number(value);
      if(!current || current.draft.fitMode !== 'crop' || !Number.isFinite(zoom)) return false;
      current.draft.zoom = clamp(zoom, settings.zoomMin, settings.zoomMax);
      settle(current.d, current.draft);
      emit();
      return true;
    }

    function setCenter(center){
      const current = editing();
      const x = Number(center && center.centerX);
      const y = Number(center && center.centerY);
      if(!current || current.draft.fitMode !== 'crop' || !geometry(current.d, current.draft)) return false;
      if(!Number.isFinite(x) || !Number.isFinite(y)) return false;
      current.draft.centerX = x;
      current.draft.centerY = y;
      settle(current.d, current.draft);
      emit();
      return true;
    }

    function moveBy(dx, dy){
      const current = editing();
      const g = current && current.draft.fitMode === 'crop' ? geometry(current.d, current.draft) : null;
      if(!g) return false;
      return setCenter(Logic.panCenter(Logic.cropRect(g), Number(dx) || 0, Number(dy) || 0));
    }

    function rotate(){
      const current = editing();
      if(!current) return false;
      const draft = current.draft;
      draft.rotation = TURNS[(TURNS.indexOf(draft.rotation) + 1) % TURNS.length];
      centre(draft);
      emit();
      return true;
    }

    function resetCrop(){
      const current = editing();
      if(!current) return false;
      const changed = current.draft.fitMode !== 'crop';
      Object.assign(current.draft, { fitMode: 'crop', zoom: settings.zoomMin, rotation: TURNS[0] });
      centre(current.draft);
      if(changed) report('image_mode', { mode: MODE_EVENTS.crop });
      emit();
      return true;
    }

    function retitle(draft, text){
      if(text !== undefined) draft.title = String(text == null ? '' : text);
      if(backEditor) backEditor.titleChanged(draft);
    }

    function setTitle(text){
      const current = editing();
      if(!current) return false;
      retitle(current.draft, text == null ? '' : text);
      emit();
      return true;
    }

    function firstDesign(d){
      return state.designs.find(x => x.status === STATUS.ready) || d;
    }

    function backState(){
      const current = editing();
      if(!current) return null;
      const { d, draft } = current;
      const first = firstDesign(d);
      return {
        design: { id: d.id, number: live().indexOf(d) + 1, title: draft.title, ownBack: draft.ownBack, ownLogo: draft.ownLogo },
        sharedBack: draft.sharedBack,
        sharedLogo: draft.sharedLogo,
        size,
        orientation: draft.orientation,
        isFirst: first === d,
        firstTitle: first === d ? draft.title : first.title
      };
    }

    function updateBack(patch){
      const current = editing();
      if(!current || !backEditor || !patch || typeof patch !== 'object') return false;
      const draft = current.draft;
      BACK_PARTS.forEach(key => {
        if(!Object.prototype.hasOwnProperty.call(patch, key)) return;
        const value = copyOf(patch[key]);
        if(value || key !== 'sharedBack') draft[key] = value;
      });
      if(Object.prototype.hasOwnProperty.call(patch, 'busy')) state.editor.busy = !!patch.busy;
      retitle(draft, Object.prototype.hasOwnProperty.call(patch, 'title') ? patch.title : undefined);
      emit();
      return true;
    }

    function measured(measure, d){
      if(typeof measure !== 'function') return null;
      const value = measure(d);
      return Number.isFinite(value) ? value : null;
    }

    function storeBack(d, draft, measure){
      state.sharedBack = draft.sharedBack;
      state.sharedLogo = draft.sharedLogo;
      state.designs.forEach(x => {
        if(x !== d && x.status !== STATUS.ready) return;
        x.logoLayout = backEditor.logoLayout(state.sharedBack, state.sharedLogo, x, measured(measure, x));
      });
      backEditor.save(storage, state.sharedBack, state.sharedLogo);
    }

    function closeEditor(){
      if(!state.editor) return false;
      state.editor = null;
      emit();
      return 'closed';
    }

    function confirmEditor(measure){
      const current = editing();
      const g = current ? geometry(current.d, current.draft) : null;
      if(!g || adding() || state.editor.busy || tooLargeFor(current.d, current.draft)) return false;
      const { d, draft } = current;
      const dpi = cropDpi(d, draft);
      Object.assign(d, {
        fitMode: draft.fitMode,
        rotation: draft.rotation,
        orientation: draft.orientation,
        zoom: draft.zoom,
        centerX: draft.centerX,
        centerY: draft.centerY,
        crop: Logic.cropForMode(draft.fitMode, g),
        title: draft.title,
        ownBack: draft.ownBack,
        ownLogo: draft.ownLogo,
        dpi,
        lowRes: Logic.isLowResolution(dpi, ArtCards.QUALITY_MIN_DPI)
      });
      if(backEditor) storeBack(d, draft, measure);
      const next = nextUnchecked(d.id);
      state.editor = next ? editorFor(next) : null;
      state.preview = { id: d.id, view: VIEWS[1], side: SIDES[0] };
      unDone();
      if(!confirmDesign(d.id)) emit();
      return true;
    }

    function variant(){
      return findVariant(variants, state.quantity, state.packaging);
    }

    function reasons(){
      return Logic.blockedReasons({
        quantity: state.quantity,
        designs: state.designs,
        variantFound: state.quantity ? !!variant() : undefined,
        rules
      });
    }

    function allocation(){
      const list = live();
      return {
        shown: !!state.quantity && list.length > 1,
        quantity: state.quantity,
        designs: list.map(d => d.id),
        problems: Logic.allocProblems(list.map(d => d.alloc), state.quantity, rules)
      };
    }

    function limits(){
      return { count: live().length, max: capacity() };
    }

    function stepDone(step){
      if(step === 1) return !!state.quantity;
      if(step === 2) return live().length > 0 && reasons().every(code => code === 'no_variant');
      return false;
    }

    function reason(){
      return reasonOf(reasons(), state.quantity, allocation().problems, rules);
    }

    function addView(){
      const add = state.add;
      const rows = [];
      if(add.status === ADD.adding){
        add.steps.forEach((step, index) => {
          if(step.kind === 'cart' || step.kind === 'draft') return;
          const d = design(step.id);
          rows.push({ kind: step.kind, name: d ? labelOf(d) : '', state: index < add.at ? 'done' : (index === add.at ? 'now' : 'wait') });
        });
      }
      return { status: add.status, rows, failed: copyOf(add.failed), done: copyOf(add.done) };
    }

    function summary(){
      const quantity = state.quantity;
      const chosen = variant();
      const plain = findVariant(variants, quantity, ArtCards.PACKAGING[0]);
      const sleeved = findVariant(variants, quantity, ArtCards.PACKAGING[1]);
      let sleeves = { code: 'none', cents: null };
      if(quantity) sleeves = plain && sleeved ? { code: 'diff', cents: sleeved.price - plain.price } : { code: 'unavailable', cents: null };
      return {
        size: size.label,
        quantity,
        designs: live().length,
        packaging: state.packaging === ArtCards.PACKAGING[1] ? 'sleeves' : 'cards',
        sleeves,
        total: chosen ? chosen.price : null,
        each: chosen ? chosen.price / quantity : null,
        rush: state.rush,
        reason: reason(),
        add: addView()
      };
    }

    function confirmedCrop(d){
      return FIT_MODES.includes(d.fitMode) && !!d.crop && CROP_KEYS.every(key => Number.isFinite(d.crop[key]));
    }

    function cardOrientation(d){
      return !size.isSquare && d.orientation === 'landscape' ? 'landscape' : 'portrait';
    }

    function clampCrop(crop){
      const x = clamp(crop.x, 0, 1);
      const y = clamp(crop.y, 0, 1);
      return {
        x,
        y,
        w: Math.max(0, x + crop.w <= 1 ? crop.w : 1 - x),
        h: Math.max(0, y + crop.h <= 1 ? crop.h : 1 - y)
      };
    }

    function cutSpec(d){
      return {
        crop: clampCrop(d.crop),
        rotation: TURNS.includes(d.rotation) ? d.rotation : TURNS[0],
        mode: d.fitMode,
        card: { size: size.isSquare ? 'square' : '5x7', orientation: cardOrientation(d) }
      };
    }

    function frontKey(d){
      return JSON.stringify([d.id, d.uploadRef, cutSpec(d)]);
    }

    function newPrepared(){
      return { sessionId: newSessionId(), fronts: new Map(), backs: new Map(), previews: new Map(), prints: new Map() };
    }

    async function send(blob, fileName, fields){
      return storedUrl(await cart.upload({ blob, fileName, fields, sessionId: adding() && prepared ? prepared.sessionId : viewSessionId }));
    }

    async function cutFront(d){
      const key = frontKey(d);
      if(prepared.fronts.has(key)) return;
      const url = storedUrl(await cart.cut(d, cutSpec(d)));
      prepared.fronts.set(key, { url });
    }

    async function keyLogos(all){
      const keys = new Map();
      const needs = logo => !!logo && !logo.key && !!logo.dataUrl;
      const keyed = async logo => {
        if(!keys.has(logo.dataUrl)) keys.set(logo.dataUrl, Promise.resolve(backEditor.logoKey(backEditor.dataUrlBytes(logo.dataUrl))));
        const key = await keys.get(logo.dataUrl);
        if(typeof key !== 'string' || !key) throw new Error('no_logo_key');
        return Object.assign({}, logo, { key });
      };
      for(const d of live()){
        const own = all ? !!d.ownLogo : !!d.ownBack;
        if(own && needs(d.ownLogo)) d.ownLogo = await keyed(d.ownLogo);
        if((all || !own) && needs(state.sharedLogo)) state.sharedLogo = await keyed(state.sharedLogo);
      }
    }

    function logoFields(logo){
      const layout = logo.layout || {};
      const pct = value => value != null ? String(value) : '';
      return {
        context: 'art-cards-logo',
        asset_type: 'logo',
        logo_position: logo.position || 'bottom',
        logo_size: logo.size || 'medium',
        logo_layout: JSON.stringify(layout),
        logo_width_pct: pct(layout.width_pct),
        logo_inset_pct: pct(layout.inset_pct),
        logo_gap_pct: pct(layout.gap_pct)
      };
    }

    async function logoUrl(logo){
      if(logoUrls.has(logo.key)) return logoUrls.get(logo.key);
      const url = await send(await toBlob(logo.dataUrl), logoFileName(logo.dataUrl), logoFields(logo));
      logoUrls.set(logo.key, url);
      return url;
    }

    async function uploadLogos(){
      if(!backEditor) return;
      await keyLogos(true);
      const logos = (state.sharedLogo ? [state.sharedLogo] : []).concat(live().map(d => d.ownLogo).filter(Boolean));
      for(const logo of logos){
        if(!logo.dataUrl) continue;
        if(!logo.key) throw new Error('no_logo_key');
        await logoUrl(logo);
      }
    }

    function logoPlace(logo){
      return logo ? { position: logo.position, size: logo.size } : null;
    }

    function logoAddress(logo){
      return logo && logo.key && logoUrls.has(logo.key) ? logoUrls.get(logo.key) : null;
    }

    function keepable(){
      return live().filter(d => !!usableRef(d.uploadRef));
    }

    function designState(d, crops, addresses){
      const item = usableRef(d.uploadRef);
      if(!item) throw new Error('not_keepable');
      const confirmed = crops && d.status === STATUS.ready;
      Object.assign(item, { name: d.name, title: d.title, alloc: Number(d.alloc) || 0, confirmed });
      if(confirmed){
        Object.assign(item, {
          fit_mode: d.fitMode,
          crop: copyOf(d.crop),
          rotation: d.rotation,
          orientation: d.orientation,
          zoom: d.zoom,
          center_x: d.centerX,
          center_y: d.centerY,
          logo_layout: copyOf(d.logoLayout)
        });
      }
      item.own_back = copyOf(d.ownBack);
      item.own_logo = logoPlace(d.ownLogo);
      item.logo_url = addresses ? logoAddress(d.ownLogo) : null;
      return item;
    }

    function stateOf(list, crops, addresses){
      return {
        v: 1,
        card: product,
        quantity: state.quantity,
        packaging: state.packaging,
        rush: state.rush,
        shared_back: copyOf(state.sharedBack),
        shared_logo: logoPlace(state.sharedLogo),
        logo_url: addresses ? logoAddress(state.sharedLogo) : null,
        designs: list.map(d => designState(d, crops, addresses))
      };
    }

    function packState(){
      return stateOf(live(), true, true);
    }

    function travelPack(keepCrops){
      return stateOf(keepable(), !!keepCrops, false);
    }

    function packExtras(){
      const shared = state.sharedLogo && state.sharedLogo.dataUrl ? state.sharedLogo : null;
      const own = {};
      keepable().forEach((d, index) => {
        const logo = d.ownLogo;
        if(!logo || !logo.dataUrl) return;
        if(shared && ((logo.key && logo.key === shared.key) || logo.dataUrl === shared.dataUrl)) return;
        own[index] = logo.dataUrl;
      });
      return { logos: { shared: shared ? shared.dataUrl : null, own } };
    }

    function rememberDraft(answer, fallback){
      const id = answer && answer.id;
      draftId = id == null || id === '' ? fallback : id;
      if(draftId == null) throw new Error('no_draft_id');
      return draftId;
    }

    async function writeDraft(status){
      await uploadLogos();
      const pack = packState();
      if(draftId !== null){
        const known = draftId;
        try {
          return rememberDraft(await drafts.update(known, { state: pack, status }), known);
        } catch(error){
          if(!error || (error.status !== 404 && error.status !== 409)) throw error;
          draftId = null;
        }
      }
      const body = { product_handle: productHandle };
      if(productId !== null) body.product_id = productId;
      body.state = pack;
      body.status = status;
      const id = rememberDraft(await drafts.create(body), null);
      guestRef({ draft: id });
      return id;
    }

    async function previewUrl(input, signature){
      if(prepared.previews.has(signature)) return prepared.previews.get(signature);
      let dataUrl = '';
      try {
        dataUrl = await renderPreview(input, { isSquare: size.isSquare });
      } catch(e){
        dataUrl = '';
      }
      let url = '';
      if(typeof dataUrl === 'string' && dataUrl){
        url = await send(await toBlob(dataUrl), 'back-preview.jpg', { context: 'art-cards-back-preview', asset_type: 'back-preview' });
      }
      prepared.previews.set(signature, url);
      return url;
    }

    async function printUrl(input, signature){
      if(prepared.prints.has(signature)) return prepared.prints.get(signature);
      let blob = null;
      let dpi = 0;
      for(const step of ArtCards.PRINT_DPI_STEPS){
        try {
          blob = await renderPrint(input, step, { isSquare: size.isSquare });
        } catch(e){
          blob = null;
        }
        if(blob){
          dpi = step;
          break;
        }
      }
      let url = '';
      if(blob){
        url = await send(blob, 'back-print.png', {
          context: 'art-cards-back-print',
          asset_type: 'back-print',
          dpi: String(dpi),
          bleed: '0',
          card_size: size.label,
          orientation: input.orientation
        });
      }
      prepared.prints.set(signature, url);
      return url;
    }

    async function prepareBack(d){
      if(!backEditor){
        prepared.backs.set(d.id, null);
        return;
      }
      await keyLogos();
      const { back, logo } = backEditor.printBack(state.sharedBack, state.sharedLogo, d);
      if(logo && !logo.key) throw new Error('no_logo_key');
      const orientation = cardOrientation(d);
      const result = { back, logo: null, previewUrl: '', printUrl: '' };
      if(logo) result.logo = { key: logo.key, position: logo.position, size: logo.size, url: await logoUrl(logo), layout: logo.layout };
      if(drawable(back, logo)){
        const input = renderInput(back, logo, orientation);
        const signature = ArtCards.backSignature({ back, logo, orientation });
        result.previewUrl = await previewUrl(input, signature);
        result.printUrl = await printUrl(input, signature);
      }
      prepared.backs.set(d.id, result);
    }

    async function addPack(){
      const chosen = variant();
      if(!chosen) throw new Error('no_variant');
      const designs = live().map(d => {
        const front = prepared.fronts.get(frontKey(d));
        const files = prepared.backs.get(d.id);
        if(!front || files === undefined) throw new Error('not_prepared');
        return {
          name: d.name,
          url: front.url,
          fitMode: d.fitMode,
          alloc: d.alloc,
          orientation: cardOrientation(d),
          back: files ? files.back : null,
          logo: files ? files.logo : null,
          backPreviewUrl: files ? files.previewUrl : '',
          backPrintUrl: files ? files.printUrl : ''
        };
      });
      const properties = ArtCards.buildProperties({
        designs,
        sizeLabel: size.label,
        quantity: state.quantity,
        rush: state.rush,
        isSquare: size.isSquare,
        customerId,
        sessionId: prepared.sessionId,
        draftId
      });
      await cart.add({ id: chosen.id, quantity: 1, properties });
    }

    function perform(step){
      if(step.kind === 'front') return cutFront(design(step.id));
      if(step.kind === 'back') return prepareBack(design(step.id));
      if(step.kind === 'draft') return writeDraft('in_cart');
      return addPack();
    }

    function finish(){
      state.add = Object.assign(idleAdd(), { status: ADD.done, done: { quantity: state.quantity, designs: live().length, rush: state.rush } });
      prepared = null;
      draftId = null;
      emit();
      try {
        Promise.resolve(cart.refresh()).catch(() => {});
      } catch(e){
        return;
      }
    }

    async function run(){
      runs += 1;
      const token = runs;
      while(token === runs && adding()){
        const step = state.add.steps[state.add.at];
        try {
          await perform(step);
        } catch(error){
          if(token !== runs) return false;
          const d = step.id ? design(step.id) : null;
          state.add = Object.assign({}, state.add, {
            status: ADD.failed,
            failed: { kind: step.kind === 'front' || step.kind === 'back' ? 'file' : 'cart', name: d ? labelOf(d) : '' }
          });
          if(state.add.failed.kind === 'file' && refusedBy(error)) state.add.failed.refused = true;
          report('error_shown', { where: 'add_to_cart', code: state.add.failed.kind });
          emit();
          return false;
        }
        if(token !== runs) return false;
        if(step.kind === 'cart'){
          finish();
          return true;
        }
        state.add.at += 1;
        emit();
      }
      return false;
    }

    function addToCart(){
      const list = live();
      if(adding() || state.editor || state.save.status === SAVE.saving || reasons().length || !list.length || !list.every(confirmedCrop)) return false;
      if(!prepared) prepared = newPrepared();
      const steps = [];
      list.forEach(d => steps.push({ kind: 'front', id: d.id }, { kind: 'back', id: d.id }));
      if(drafts) steps.push({ kind: 'draft' });
      steps.push({ kind: 'cart' });
      state.add = Object.assign(idleAdd(), { status: ADD.adding, steps });
      report('add_clicked', { quantity: state.quantity, designs_total: list.length, value: variant().price / 100, currency });
      emit();
      return run();
    }

    function retryAdd(){
      if(state.add.status !== ADD.failed || !prepared || state.editor) return false;
      state.add = Object.assign({}, state.add, { status: ADD.adding, failed: null });
      emit();
      return run();
    }

    function cancelAdd(){
      if(state.add.status !== ADD.failed) return false;
      state.add = idleAdd();
      emit();
      return true;
    }

    function startOver(){
      if(adding()) return false;
      state.designs.forEach(stopUpload);
      state.designs.forEach(releasePreview);
      state.designs = [];
      state.quantity = null;
      state.packaging = ArtCards.PACKAGING[0];
      state.rush = false;
      state.pending = null;
      state.editor = null;
      state.add = idleAdd();
      state.preview = idlePreview();
      prepared = null;
      draftId = null;
      emit();
      return true;
    }

    function restoredLogo(place, dataUrl){
      if(!backEditor || !place || typeof place !== 'object' || typeof dataUrl !== 'string' || !dataUrl) return null;
      return backEditor.cleanLogo({ dataUrl, position: place.position, size: place.size });
    }

    function storedCrop(item){
      const crop = item.crop;
      return FIT_MODES.includes(item.fit_mode) && !!crop && typeof crop === 'object' && CROP_KEYS.every(key => Number.isFinite(crop[key])) &&
        [item.zoom, item.center_x, item.center_y].every(Number.isFinite);
    }

    function restoreCrop(d, item){
      const draft = {
        fitMode: item.fit_mode,
        rotation: TURNS.includes(item.rotation) ? item.rotation : TURNS[0],
        orientation: !size.isSquare && item.orientation === 'landscape' ? 'landscape' : 'portrait',
        zoom: item.zoom,
        centerX: item.center_x,
        centerY: item.center_y
      };
      const dpi = cropDpi(d, draft);
      Object.assign(d, draft, {
        crop: { x: item.crop.x, y: item.crop.y, w: item.crop.w, h: item.crop.h },
        logoLayout: copyOf(item.logo_layout),
        dpi,
        lowRes: Logic.isLowResolution(dpi, ArtCards.QUALITY_MIN_DPI)
      });
    }

    function finishRestore(id, result){
      const d = design(id);
      const kept = restoring.get(id);
      if(!d || !kept || d.status !== STATUS.uploading) return;
      const sized = applySize(d, result && result.width, result && result.height);
      const library = httpsUrl(result && result.libraryUrl);
      if(library) d.libraryUrl = library;
      const preview = httpsUrl(result && result.previewUrl);
      if(sized && preview) d.previewUrl = preview;
      else if(!sized) d.previewUrl = d.libraryUrl || preview;
      d.progress = 100;
      if(kept.keep && kept.item.confirmed === true && sized && storedCrop(kept.item)){
        restoreCrop(d, kept.item);
        d.status = STATUS.ready;
      } else {
        d.status = STATUS.unchecked;
      }
      emit();
    }

    function failRestore(id){
      const d = design(id);
      if(!d || d.status !== STATUS.uploading) return;
      d.status = STATUS.failed;
      emit();
    }

    function startRestore(d){
      d.status = STATUS.uploading;
      d.progress = 100;
      let pending;
      try {
        pending = Promise.resolve(prepareRestored(d));
      } catch(e){
        pending = Promise.reject(e);
      }
      return pending.then(result => finishRestore(d.id, result), () => failRestore(d.id));
    }

    function restorePack(pack, options){
      if(adding() || state.editor || !pack || typeof pack !== 'object' || pack.v !== 1 || !Array.isArray(pack.designs)) return false;
      const opts = options || {};
      const logos = opts.extras && opts.extras.logos && typeof opts.extras.logos === 'object' ? opts.extras.logos : null;
      const own = logos && logos.own && typeof logos.own === 'object' ? logos.own : {};
      const keep = !!opts.keepCrops && pack.card === product;
      state.designs.forEach(stopUpload);
      state.designs.forEach(releasePreview);
      state.designs = [];
      started.clear();
      preparing.clear();
      restoring.clear();
      state.pending = null;
      state.preview = idlePreview();
      quiet = true;
      try {
        const packaging = ArtCards.PACKAGING.includes(pack.packaging) ? pack.packaging : null;
        if(packaging) setPackaging(packaging);
        const quantity = Number.isInteger(pack.quantity) && pack.quantity > 0 && findVariant(variants, pack.quantity, state.packaging) ? pack.quantity : null;
        if(quantity) setQuantity(quantity);
        state.rush = !!pack.rush;
      } finally {
        quiet = false;
      }
      if(backEditor){
        if(pack.shared_back && typeof pack.shared_back === 'object' && !Array.isArray(pack.shared_back)) state.sharedBack = copyOf(pack.shared_back);
        if(logos) state.sharedLogo = restoredLogo(pack.shared_logo, logos.shared);
      }
      const added = [];
      pack.designs.slice(0, rules.maxDesigns).forEach((item, index) => {
        const ref = usableRef(item);
        if(!ref) return;
        const image = own[index] !== undefined ? own[index] : (item.own_logo && logos ? logos.shared : null);
        const d = makeDesign({
          name: String(item.name || ''),
          title: String(item.title || ''),
          source: ref.library_image_id != null ? 'library' : 'file',
          uploadRef: ref,
          progress: 100,
          alloc: Number(item.alloc) || 0,
          ownBack: copyOf(item.own_back),
          ownLogo: restoredLogo(item.own_logo, image)
        });
        state.designs.push(d);
        restoring.set(d.id, { item, keep });
        added.push(d);
      });
      const total = added.reduce((sum, d) => sum + d.alloc, 0);
      if(!state.quantity || total !== state.quantity) resplit();
      unDone();
      emit();
      return { added: added.map(d => d.id), done: Promise.all(added.map(startRestore)) };
    }

    function readyDesigns(){
      return state.designs.filter(d => d.status === STATUS.ready);
    }

    function shownDesign(){
      const list = readyDesigns();
      return list.find(d => d.id === state.preview.id) || list[0] || null;
    }

    function showPreview(id){
      const d = design(id);
      if(!d || d.status !== STATUS.ready) return false;
      state.preview = { id, view: VIEWS[1], side: SIDES[0] };
      emit();
      return true;
    }

    function setPreviewView(view){
      if(!VIEWS.includes(view) || (view === VIEWS[1] && !readyDesigns().length)) return false;
      state.preview.view = view;
      emit();
      return true;
    }

    function setPreviewSide(side){
      if(!SIDES.includes(side)) return false;
      state.preview.side = side;
      emit();
      return true;
    }

    function frontPlacement(d){
      if(!confirmedCrop(d) || !d.width || !d.height) return null;
      const g = geometry(d, { rotation: TURNS.includes(d.rotation) ? d.rotation : TURNS[0], orientation: cardOrientation(d), zoom: d.zoom, centerX: d.centerX, centerY: d.centerY });
      if(d.fitMode === 'crop' && ![d.zoom, d.centerX, d.centerY].every(Number.isFinite)){
        Object.assign(g, { zoom: Logic.zoomOf(d.crop, g) }, Logic.centerOf(d.crop));
      }
      return Logic.stagePlacement(d.fitMode, g);
    }

    function backOf(d){
      if(!backEditor) return null;
      const { back, logo } = backEditor.printBack(state.sharedBack, state.sharedLogo, d);
      const input = renderInput(back, logo, cardOrientation(d));
      const key = JSON.stringify(Object.assign({}, input, { logoDataUrl: logo && logo.key ? null : input.logoDataUrl, logoKey: (logo && logo.key) || '' }));
      return { input, empty: !drawable(back, logo), key };
    }

    function previewView(){
      const d = shownDesign();
      const side = state.preview.side;
      if(!d){
        return { hasCard: false, view: VIEWS[0], side, id: null, name: '', cards: null, shape: null, fitMode: null, previewUrl: '', placement: null, back: null };
      }
      return {
        hasCard: true,
        view: state.preview.view,
        side,
        id: d.id,
        name: labelOf(d),
        cards: state.quantity ? d.alloc : null,
        shape: size.isSquare ? SHAPES[0] : cardOrientation(d),
        fitMode: FIT_MODES.includes(d.fitMode) ? d.fitMode : null,
        previewUrl: d.previewUrl,
        placement: frontPlacement(d),
        back: backOf(d)
      };
    }

    function drawBack(input){
      let pending;
      try {
        pending = Promise.resolve(renderPreview(input, { isSquare: size.isSquare }));
      } catch(e){
        pending = Promise.resolve('');
      }
      return pending.then(image => typeof image === 'string' ? image : '', () => '');
    }

    function previewBack(){
      const d = shownDesign();
      const back = d ? backOf(d) : null;
      if(!back || back.empty) return Promise.resolve({ key: back ? back.key : '', image: '' });
      if(!backImages.has(back.key)) backImages.set(back.key, drawBack(back.input));
      return backImages.get(back.key).then(image => ({ key: back.key, image }));
    }

    function canSave(){
      const list = live();
      return !adding() && !state.editor && state.save.status !== SAVE.saving && state.add.status !== ADD.failed && list.length > 0 &&
        list.every(d => (d.status === STATUS.unchecked || d.status === STATUS.ready) && !!usableRef(d.uploadRef));
    }

    function savedAs(result, total){
      state.save = { status: SAVE.idle };
      if(result === 'saved') report('design_saved', { designs_total: total, logged_in: true });
      else report('error_shown', { where: 'save', code: 'save_failed' });
      emit();
      return result;
    }

    function saveDesigns(){
      if(!canSave()) return Promise.resolve(false);
      if(customerId === 'guest') return Promise.resolve('sign_in');
      const total = live().length;
      state.save = { status: SAVE.saving };
      emit();
      const pending = drafts ? writeDraft('draft') : notWired();
      return pending.then(() => savedAs('saved', total), () => savedAs('failed', total));
    }

    function subscribe(listener){
      if(typeof listener !== 'function') return () => false;
      listeners.add(listener);
      return () => listeners.delete(listener);
    }

    return {
      get state(){ return state; },
      rules,
      size,
      product,
      variants,
      design,
      live,
      quantities: () => quantities(variants, state.packaging),
      subscribe,
      track: report,
      addFiles,
      addLibrary,
      remove: id => remove(id),
      retry,
      setDimensions,
      setQuantity,
      toggleKeep,
      canApplyKeep,
      applyKeep,
      cancelKeep,
      setAlloc,
      setPackaging,
      setRush,
      applySwitch,
      quantityOptions,
      confirmDesign,
      variant,
      reasons,
      allocation,
      limits,
      stepDone,
      summary,
      addToCart,
      retryAdd,
      cancelAdd,
      startOver,
      openEditor: openDesignEditor,
      editor: editorView,
      setEditorTab,
      setFitMode,
      setOrientation,
      setZoom,
      setCenter,
      moveBy,
      rotate,
      resetCrop,
      setTitle,
      backState,
      updateBack,
      closeEditor,
      confirmEditor,
      showPreview,
      setPreviewView,
      setPreviewSide,
      previewView,
      previewBack,
      canSave,
      saveDesigns,
      packState,
      travelPack,
      packExtras,
      restorePack
    };
  }

  function readJson(node){
    if(!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch(e){
      return null;
    }
  }

  function numberFormat(locales, options){
    const list = (Array.isArray(locales) ? locales : []).filter(Boolean).concat([undefined]);
    for(const locale of list){
      try {
        return new Intl.NumberFormat(locale, options);
      } catch(e){
        continue;
      }
    }
    return null;
  }

  function localStore(){
    try {
      return window.localStorage;
    } catch(e){
      return null;
    }
  }

  function serverTransport(config, summaryConfig){
    const server = window.PrintBuilderServer;
    if(!server || typeof server.createTransport !== 'function') return null;
    if(typeof config.apiBase !== 'string' || !config.apiBase) return null;
    return server.createTransport({
      apiBase: config.apiBase,
      customerId: summaryConfig.customerId == null ? null : summaryConfig.customerId,
      productType: config.productType,
      productHandle: config.handle,
      productTitle: config.productTitle,
      maxBytes: config.uploadMaxBytes,
      fetch: (url, init) => window.fetch(url, init),
      XMLHttpRequest: window.XMLHttpRequest,
      storage: localStore(),
      tokenReady: () => window.kfTokenReady,
      wait: ms => new Promise(resolve => window.setTimeout(resolve, ms))
    });
  }

  function formats(config){
    const money = config.currency ? numberFormat(config.locales, { style: 'currency', currency: config.currency }) : null;
    const count = numberFormat(config.locales);
    return {
      money: cents => money ? money.format(cents / 100) : (cents / 100).toFixed(2),
      number: value => count ? count.format(value) : String(value)
    };
  }

  function postCart(body){
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(response => {
      if(response.ok) return response.json().catch(() => null);
      return response.json().catch(() => ({})).then(error => {
        throw new Error((error && (error.description || error.message)) || 'cart_error');
      });
    });
  }

  function refreshCart(){
    const content = document.querySelector('[data-cart-content]');
    const itemCount = () => fetch('/cart.js').then(response => response.json()).then(cart => cart.item_count);
    if(content && typeof window.updateCartHtml === 'function'){
      const sectionId = content.getAttribute('data-section') || 'ajax-cart';
      return Promise.all([
        fetch('/cart?sections=' + encodeURIComponent(sectionId)).then(response => response.json()),
        itemCount()
      ]).then(([sections, count]) => window.updateCartHtml(content, { sections, item_count: count }, sectionId));
    }
    if(typeof window.cartCountUpdate === 'function') return itemCount().then(count => window.cartCountUpdate(count));
    return Promise.resolve();
  }

  function scrollBehavior(){
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    } catch(e){
      return 'auto';
    }
  }

  function mount(id){
    if(typeof document === 'undefined' || !id || !Logic || !ArtCards) return null;
    const parts = Array.from(document.querySelectorAll('[data-pb-id]')).filter(node => node.getAttribute('data-pb-id') === id);
    if(!parts.length || parts.some(node => node.hasAttribute('data-pb-mounted'))) return null;
    parts.forEach(node => node.setAttribute('data-pb-mounted', ''));

    const query = selector => {
      for(const part of parts){
        const found = part.querySelector(selector);
        if(found) return found;
      }
      return null;
    };
    const queryAll = selector => parts.reduce((all, part) => all.concat(Array.from(part.querySelectorAll(selector))), []);
    const find = name => query('[data-pb="' + name + '"]');
    const clone = name => {
      const tpl = query('template[data-pb-tpl="' + name + '"]');
      return tpl && tpl.content && tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
    };
    const on = (target, type, handler) => { if(target) target.addEventListener(type, handler); };
    const setText = (node, text) => { if(node) node.textContent = text; };

    const config = readJson(query('script[data-pb-config]')) || {};
    const variants = readJson(query('script[data-pb-variants]')) || [];
    const strings = Object.assign({}, ...queryAll('script[data-pb-strings]').map(node => readJson(node) || {}));
    const editorConfig = readJson(query('script[data-pb-editor-config]'));
    const summaryConfig = readJson(query('script[data-pb-summary-config]')) || {};
    const backConfig = readJson(query('script[data-pb-bk-config]')) || {};
    const accountConfig = readJson(query('script[data-pb-account-config]')) || {};
    const prefix = typeof config.eventPrefix === 'string' ? config.eventPrefix : '';
    const backLimits = backConfig.maxLength && typeof backConfig.maxLength === 'object' ? backConfig.maxLength : null;
    const steps = editorConfig || {};
    const format = formats(config);
    const t = (key, values) => fill(strings[key], values);
    const backEditor = window.ArtCardsBackEditor && typeof window.ArtCardsBackEditor.mount === 'function' ? window.ArtCardsBackEditor : null;
    const transport = serverTransport(config, summaryConfig);
    const signedIn = summaryConfig.customerId != null && summaryConfig.customerId !== '';
    let savedHere = false;
    const builder = createBuilder({
      variants,
      cardSize: config.cardSize,
      handle: config.handle,
      editor: editorConfig,
      backEditor,
      backLimits,
      storage: backEditor ? localStore() : null,
      customerId: summaryConfig.customerId,
      currency: config.currency,
      track: (name, params) => {
        if(name === 'design_saved') savedHere = true;
        if(prefix) sendEvent(window, prefix, name, params);
      },
      upload: transport ? transport.upload : (d, onProgress) => api.serverUploadStub(d, onProgress),
      prepareLibrary: transport ? transport.prepareLibrary : undefined,
      prepareRestored: transport ? transport.prepareRestored : undefined,
      cancelUpload: transport ? transport.cancel : undefined,
      drafts: transport ? {
        create: body => transport.createDraft({ productHandle: body.product_handle, productId: body.product_id, state: body.state, status: body.status }),
        update: (id, body) => transport.updateDraft(id, body)
      } : undefined,
      productHandle: config.handle,
      productId: config.productId,
      onGuestRef: ref => rememberGuestRef(ref),
      limits: transport ? { maxBytes: config.uploadMaxBytes, maxPixels: config.maxPixels } : undefined,
      preview: file => URL.createObjectURL(file),
      release: url => { if(String(url).indexOf('blob:') === 0) URL.revokeObjectURL(url); },
      cart: { cut: transport ? transport.cut : notWired, upload: transport ? transport.uploadFile : notWired, add: postCart, refresh: refreshCart }
    });
    const rules = builder.rules;
    const el = {
      tiles: find('tiles'),
      more: find('more'),
      rule: find('rule-text'),
      stepTitle: find('step-1-title'),
      keepDialog: find('keep-dialog'),
      keepText: find('keep-text'),
      keepList: find('keep-list'),
      keepCancel: find('keep-cancel'),
      keepApply: find('keep-apply'),
      switchDialog: find('switch-dialog'),
      switchTitle: find('switch-title'),
      switchText: find('switch-text'),
      switchGo: find('switch-go'),
      switchStay: find('switch-stay'),
      switchWait: find('switch-wait'),
      toast: find('toast'),
      toastText: find('toast-text'),
      toastLink: find('toast-link'),
      toastClose: find('toast-close'),
      announce: find('announce'),
      count: find('design-count'),
      libraryNote: find('library-note'),
      libraryNoteOk: find('library-note-ok'),
      drop: find('drop'),
      fileInput: find('file-input'),
      library: find('library'),
      designs: find('designs'),
      alloc: find('alloc'),
      allocText: find('alloc-text'),
      editor: find('editor'),
      edHeading: find('ed-heading'),
      edFile: find('ed-file'),
      edTabFront: find('ed-tab-front'),
      edTabBack: find('ed-tab-back'),
      edClose: find('ed-close'),
      edFront: find('front-panel'),
      edBack: find('back-panel'),
      edPanel: find('ed-panel'),
      edCard: find('ed-card'),
      edFrame: find('ed-frame'),
      edPic: find('ed-pic'),
      edHint: find('ed-hint'),
      edTitle: find('ed-title'),
      edModeNote: find('ed-mode-note'),
      edOrient: find('ed-orient'),
      edZoom: find('ed-zoom'),
      edZoomOut: find('ed-zoom-out'),
      edZoomIn: find('ed-zoom-in'),
      edRotate: find('ed-rotate'),
      edReset: find('ed-reset'),
      edMoveNote: find('ed-move-note'),
      edDpi: find('ed-dpi'),
      edDpiTitle: find('ed-dpi-title'),
      edDpiText: find('ed-dpi-text'),
      edCancel: find('ed-cancel'),
      edNext: find('ed-next'),
      edBackStep: find('ed-back'),
      edConfirm: find('ed-confirm'),
      packs: find('packs'),
      sleevesPrice: find('pack-sleeves-price'),
      summary: find('summary'),
      sumTitle: find('sum-title'),
      sumSize: find('sum-size'),
      sumCards: find('sum-cards'),
      sumDesigns: find('sum-designs'),
      sumPack: find('sum-pack'),
      rush: find('rush'),
      sumTotal: find('sum-total'),
      sumEach: find('sum-each'),
      addCart: find('add-cart'),
      why: find('why'),
      whyText: find('why-text'),
      adding: find('adding'),
      addingList: find('adding-list'),
      addFailed: find('add-failed'),
      addFailedTitle: find('add-failed-title'),
      addFailedText: find('add-failed-text'),
      addRetry: find('add-retry'),
      addCancel: find('add-cancel'),
      addDone: find('add-done'),
      addDoneTitle: find('add-done-title'),
      addDoneText: find('add-done-text'),
      cartLink: find('cart-link'),
      addAgain: find('add-again'),
      bar: find('bar'),
      barTotal: find('bar-total'),
      barSub: find('bar-sub'),
      barGo: find('bar-go'),
      barSpace: find('bar-space'),
      save: find('save'),
      signin: find('signin-dialog'),
      signinOpen: find('signin-open'),
      signinClose: find('signin-close'),
      signinWait: find('signin-wait'),
      ycPhotos: find('yc-photos'),
      ycCard: find('yc-card'),
      ycView: find('yc-view'),
      ycShowFront: find('yc-show-front'),
      ycShowBack: find('yc-show-back'),
      ycFront: find('yc-front'),
      ycPic: find('yc-pic'),
      ycBack: find('yc-back'),
      ycBackImg: find('yc-back-img'),
      ycBlank: find('yc-blank'),
      ycCaption: find('yc-caption')
    };
    const ycPart = parts.find(node => node.getAttribute('data-pb-part') === 'preview') || null;
    const sizeLinks = queryAll('[data-pb-size]');
    const signinLinks = queryAll('[data-pb="signin-link"]');
    const fitButtons = queryAll('[data-pb-fit]');
    const orientButtons = queryAll('[data-pb-orient]');
    const moveButtons = queryAll('[data-pb-move]');
    const packButtons = queryAll('[data-pb-pack]');
    const lockedParts = parts.filter(node => LOCKED_PARTS.includes(node.getAttribute('data-pb-part')));
    const tileButtons = new Map();
    const rows = new Map();
    const addRows = [];
    const openers = new Map();
    const fallbacks = new Set();
    const pointers = new Map();
    let addShown = builder.state.add.status;
    let toastTimer = null;
    let optionsFor = null;
    let edFrom = null;
    let edShown = null;
    let edSession = null;
    let edPicSrc = '';
    let backHandle = null;
    let backFor = null;
    let drag = null;
    let pinch = null;
    let ycSrc = '';
    let ycHosted = false;
    let ycShown = { key: null, image: '' };
    let ycAsked = null;
    let batch = null;
    let opening = false;
    let packTimer = null;
    let packOff = false;
    let leaving = false;
    const titleMax = Number(backLimits && backLimits.line2);
    if(el.edTitle && titleMax > 0) el.edTitle.maxLength = titleMax;

    function showToast(message, href){
      if(!message) return;
      setText(el.announce, message);
      if(!el.toast) return;
      setText(el.toastText, message);
      if(el.toastLink){
        const link = typeof href === 'string' ? href : '';
        if(link) el.toastLink.setAttribute('href', link);
        else el.toastLink.removeAttribute('href');
        el.toastLink.hidden = !link;
      }
      el.toast.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(hideToast, TOAST_MS);
    }

    function hideToast(){
      clearTimeout(toastTimer);
      if(el.toast) el.toast.hidden = true;
    }

    function moveToBody(target, attribute){
      if(!target || !document.body) return;
      Array.from(document.body.children).forEach(node => {
        if(node !== target && node.getAttribute(attribute) === id) node.remove();
      });
      if(target.parentNode !== document.body) document.body.appendChild(target);
    }

    function moveToast(){
      moveToBody(el.toast, 'data-pb-toast-for');
    }

    function restoreFocus(dialog){
      const target = openers.get(dialog);
      openers.delete(dialog);
      if(target && typeof target.focus === 'function' && document.contains(target)) target.focus();
    }

    function isOpen(dialog){
      return !!dialog && (dialog.open === true || dialog.hasAttribute('open'));
    }

    function fallbackKeys(event){
      const dialog = event.currentTarget;
      if(event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      if(dialog.dispatchEvent(new Event('cancel', { cancelable: true }))) closeDialog(dialog);
    }

    function showDialog(dialog, from){
      if(!dialog || isOpen(dialog)) return;
      openers.set(dialog, from || document.activeElement);
      if(typeof dialog.showModal === 'function'){
        dialog.showModal();
        return;
      }
      dialog.setAttribute('open', '');
      if(!fallbacks.has(dialog)){
        fallbacks.add(dialog);
        dialog.addEventListener('keydown', fallbackKeys);
      }
      const first = dialog.querySelector(FOCUSABLE);
      if(first) first.focus();
    }

    function closeDialog(dialog){
      if(!isOpen(dialog)) return;
      if(typeof dialog.close === 'function'){
        dialog.close();
        return;
      }
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }

    function trapFocus(event, box){
      const items = Array.from(box.querySelectorAll(FOCUSABLE)).filter(node => node.getClientRects().length > 0);
      if(!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if(event.shiftKey && (active === first || active === box)){
        event.preventDefault();
        last.focus();
      } else if(!event.shiftKey && active === last){
        event.preventDefault();
        first.focus();
      }
    }

    function renderKeep(){
      if(el.keepApply) el.keepApply.disabled = !builder.canApplyKeep();
    }

    function keepChoice(d){
      const node = clone('keep-choice');
      if(!node) return null;
      const input = node.querySelector('[data-pb-choice="input"]');
      setText(node.querySelector('[data-pb-choice="name"]'), d.title || d.name || t('library_name'));
      if(input){
        input.checked = builder.state.pending.keep.includes(d.id);
        input.addEventListener('change', () => builder.toggleKeep(d.id, input.checked));
      }
      return node;
    }

    function openKeepDialog(from){
      const pending = builder.state.pending;
      if(!pending || !el.keepDialog) return;
      const values = { quantity: format.number(pending.quantity), allowed: pending.allowed };
      setText(el.keepText, t(pending.allowed === 1 ? 'keep_text_one' : 'keep_text_many', values));
      setText(el.keepCancel, builder.state.quantity
        ? t('keep_cancel', { quantity: format.number(builder.state.quantity) })
        : t('keep_cancel_none'));
      setText(el.keepApply, t('keep_apply', values));
      if(el.keepList) el.keepList.replaceChildren(...builder.live().map(keepChoice).filter(Boolean));
      renderKeep();
      showDialog(el.keepDialog, from);
    }

    function chooseQuantity(quantity, from){
      const result = builder.setQuantity(quantity);
      if(result.choose) openKeepDialog(from);
    }

    function tilePart(node, name){
      return node.querySelector('[data-pb-tile="' + name + '"]');
    }

    function buildTiles(){
      if(!el.tiles) return;
      const packaging = ArtCards.PACKAGING[0];
      (Array.isArray(config.tiles) ? config.tiles : []).forEach(quantity => {
        const node = quantity > 0 ? clone('tile') : null;
        if(!node) return;
        const v = findVariant(variants, quantity, packaging);
        const allowed = Logic.maxDesigns(quantity, rules);
        const each = tilePart(node, 'each');
        const badge = tilePart(node, 'badge');
        setText(tilePart(node, 'qty'), format.number(quantity));
        setText(tilePart(node, 'pack'), v ? format.money(v.price) : t('tile_unavailable'));
        setText(each, v ? t('tile_each', { price: format.money(v.price / quantity) }) : '');
        if(each) each.hidden = !v;
        setText(tilePart(node, 'designs'), allowed === 1 ? t('tile_one') : t('tile_many', { count: allowed }));
        if(badge) badge.hidden = quantity !== config.badge;
        node.disabled = !v;
        node.addEventListener('click', () => chooseQuantity(quantity, node));
        el.tiles.appendChild(node);
        tileButtons.set(quantity, node);
      });
    }

    function buildOptions(){
      if(!el.more || optionsFor === builder.state.packaging) return;
      optionsFor = builder.state.packaging;
      Array.from(el.more.options).forEach(option => { if(option.value) option.remove(); });
      builder.quantityOptions().forEach(({ quantity, variant: v }) => {
        const option = clone('quantity-option');
        if(!option) return;
        option.value = String(quantity);
        option.textContent = t('option', {
          quantity: format.number(quantity),
          price: format.money(v.price),
          each: format.money(v.price / quantity)
        });
        el.more.appendChild(option);
      });
    }

    function readSession(key){
      try {
        return JSON.parse(window.sessionStorage.getItem(key) || 'null');
      } catch(e){
        return null;
      }
    }

    function removeSession(key){
      try {
        window.sessionStorage.removeItem(key);
      } catch(e){
        return;
      }
    }

    function writeSession(key, value){
      window.sessionStorage.setItem(key, JSON.stringify(value));
    }

    function keepSession(key, record){
      const bare = Object.assign({}, record);
      delete bare.extras;
      const attempts = [record, bare];
      if(key === SWITCH_KEY) attempts.push({ from: record.from, quantity: record.quantity, packaging: record.packaging });
      return attempts.some(value => {
        try {
          writeSession(key, value);
          return true;
        } catch(e){
          return false;
        }
      });
    }

    function guestList(){
      const saved = readSession(GUEST_KEY);
      const list = saved && typeof saved === 'object' ? saved : {};
      return {
        uploads: Array.isArray(list.uploads) ? list.uploads.filter(item => item && typeof item === 'object') : [],
        drafts: Array.isArray(list.drafts) ? list.drafts : []
      };
    }

    function rememberGuestRef(ref){
      const list = guestList();
      if(ref.upload){
        const same = item => item.upload_id === ref.upload.upload_id && item.object_name === ref.upload.object_name;
        list.uploads = list.uploads.filter(item => !same(item)).concat([{ upload_id: ref.upload.upload_id, object_name: ref.upload.object_name }]).slice(-GUEST_MAX);
      }
      if(ref.draft != null) list.drafts = list.drafts.filter(id => id !== ref.draft).concat([ref.draft]).slice(-GUEST_MAX);
      try {
        writeSession(GUEST_KEY, list);
      } catch(e){
        return;
      }
    }

    function claimGuestRefs(){
      const list = guestList();
      if(!transport || !signedIn || (!list.uploads.length && !list.drafts.length)) return Promise.resolve();
      const recent = items => items.slice(-GUEST_MAX).reverse();
      return Promise.resolve(transport.claim({ uploads: recent(list.uploads), drafts: recent(list.drafts) }))
        .then(() => removeSession(GUEST_KEY), () => {});
    }

    function uploading(){
      return builder.live().some(d => d.status === STATUS.uploading);
    }

    function stashPack(){
      const pack = builder.travelPack(true);
      if(!pack.designs.length) return;
      keepSession(STASH_KEY, { v: 1, purpose: 'sign_in', handle: config.handle, at: Date.now(), pack, extras: builder.packExtras() });
    }

    function readStash(){
      const stash = readSession(STASH_KEY);
      removeSession(STASH_KEY);
      if(!stash || typeof stash !== 'object' || stash.purpose !== 'sign_in' || stash.handle !== config.handle) return null;
      const age = Date.now() - Number(stash.at);
      return Number.isFinite(age) && age >= 0 && age < STASH_MAX_AGE ? stash : null;
    }

    function returnFromSignIn(stash, claimed){
      const result = builder.restorePack(stash.pack, { keepCrops: true, extras: stash.extras });
      if(!result || !result.added.length || !signedIn) return;
      Promise.all([result.done, claimed]).then(() => {
        let unsubscribe = () => {};
        const attempt = () => {
          const list = builder.live();
          if(savedHere || !list.length){ unsubscribe(); return; }
          if(result.added.some(id => builder.design(id) && builder.design(id).status === STATUS.failed)){
            unsubscribe();
            noticeSave('failed');
            return;
          }
          if(builder.state.editor || builder.state.add.status === ADD.adding || builder.state.add.status === ADD.failed || builder.state.save.status === SAVE.saving || uploading()) return;
          unsubscribe();
          builder.saveDesigns().then(outcome => noticeSave(outcome === 'saved' ? 'saved' : 'failed'));
        };
        unsubscribe = builder.subscribe(attempt);
        attempt();
      });
    }

    function rememberSwitch(){
      keepSession(SWITCH_KEY, {
        from: config.handle,
        quantity: builder.state.quantity,
        packaging: builder.state.packaging,
        pack: builder.travelPack(false),
        extras: builder.packExtras()
      });
    }

    function openSwitchDialog(link){
      const label = link.getAttribute('data-pb-size-title') || link.textContent.trim();
      setText(el.switchTitle, t('switch_title', { size: label }));
      setText(el.switchText, t('switch_text', { size: label }));
      if(el.switchGo) el.switchGo.setAttribute('href', link.getAttribute('href'));
      showDialog(el.switchDialog, link);
    }

    function restoreSwitch(ignorePack){
      const saved = readSession(SWITCH_KEY);
      removeSession(SWITCH_KEY);
      if(!saved || typeof saved !== 'object' || saved.from === config.handle) return false;
      builder.applySwitch(saved);
      const restored = !ignorePack && saved.pack ? builder.restorePack(saved.pack, { keepCrops: false, extras: saved.extras }) : null;
      builder.track('size_switched', { from_product: productName(!builder.size.isSquare), to_product: builder.product });
      const current = sizeLinks.find(link => link.getAttribute('aria-current') === 'page');
      if(current) showToast(t(restored && restored.added.length ? 'switched_designs' : 'switched', { size: current.getAttribute('data-pb-size-short') || current.textContent.trim() }));
      return !!(restored && restored.added.length);
    }

    function ownPack(){
      const record = readSession(PACK_KEY);
      return !!record && typeof record === 'object' && record.handle === config.handle;
    }

    function forgetPack(){
      if(ownPack()) removeSession(PACK_KEY);
    }

    function keepPack(){
      packTimer = null;
      if(builder.state.add.status === ADD.done) return;
      const pack = builder.travelPack(true);
      if(!pack.designs.length){
        forgetPack();
        return;
      }
      if(uploading() || builder.live().some(d => d.status === STATUS.failed)) return;
      keepSession(PACK_KEY, { v: 1, handle: config.handle, at: Date.now(), pack, extras: builder.packExtras() });
    }

    function rememberPack(){
      if(packOff) return;
      if(builder.state.add.status === ADD.done){
        forgetPack();
        return;
      }
      if(packTimer === null) packTimer = setTimeout(keepPack, PACK_DELAY_MS);
    }

    function leavePack(){
      packOff = true;
      clearTimeout(packTimer);
      packTimer = null;
      forgetPack();
    }

    function restoreKept(){
      const record = readSession(PACK_KEY);
      if(!record || typeof record !== 'object' || record.handle !== config.handle || !record.pack || typeof record.pack !== 'object' || !Array.isArray(record.pack.designs) || !record.pack.designs.length) return;
      const age = Date.now() - Number(record.at);
      if(!Number.isFinite(age) || age < 0 || age >= STASH_MAX_AGE) return;
      const result = builder.restorePack(record.pack, { keepCrops: true, extras: record.extras });
      if(result && result.added.length) showToast(t('restored'));
    }

    function renderWait(){
      const busy = uploading();
      signinLinks.concat(el.switchGo ? [el.switchGo] : []).forEach(link => {
        if(busy){
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('disabled', '');
        } else {
          link.removeAttribute('aria-disabled');
          link.removeAttribute('disabled');
        }
      });
      [[el.signinWait, 'signin_wait'], [el.switchWait, 'switch_wait']].forEach(([node, key]) => {
        if(!node) return;
        if(node.hidden !== !busy) node.hidden = !busy;
        const text = busy ? t(key) : '';
        if(node.textContent !== text) node.textContent = text;
      });
    }

    function warnLeaving(event){
      event.preventDefault();
      event.returnValue = '';
    }

    function renderLeaving(){
      const busy = uploading() || builder.state.add.status === ADD.adding;
      if(busy === leaving) return;
      leaving = busy;
      if(busy) window.addEventListener('beforeunload', warnLeaving);
      else window.removeEventListener('beforeunload', warnLeaving);
    }

    function armBatch(result){
      batch = result.added.slice();
      openBatch();
    }

    function batchTarget(){
      if(!batch) return null;
      batch = batch.filter(designId => {
        const d = builder.design(designId);
        return !!d && (d.status === STATUS.uploading || d.status === STATUS.unchecked);
      });
      if(!batch.length) batch = null;
      const first = (batch || []).map(designId => builder.design(designId)).find(d => d.status === STATUS.unchecked);
      return first ? first.id : null;
    }

    function openBatch(){
      if(opening) return;
      const designId = batchTarget();
      if(!designId || builder.editor() || builder.state.add.status === ADD.adding || builder.state.save.status === SAVE.saving || builder.state.pending) return;
      if(document.querySelector('dialog[open]') || document.querySelector('.lib-picker:not(.hidden)')) return;
      opening = true;
      try {
        openEditorFor(designId);
      } finally {
        opening = false;
      }
    }

    function closeByHand(){
      batch = null;
      builder.closeEditor();
    }

    function limitMessage(limit){
      const sentences = [];
      if(limit.quantity && limit.cap > limit.allowed){
        sentences.push(t('limit_quantity', {
          quantity: format.number(limit.quantity),
          allowed: limit.allowed === 1 ? t('design_one') : t('design_many', { count: limit.allowed }),
          cap: limit.cap,
          need: format.number(limit.need)
        }));
      }
      if(limit.overMax) sentences.push(t('limit_pack', { max: rules.maxDesigns }));
      sentences.push(limit.skipped === 1 ? t('skipped_one') : t('skipped_many', { count: limit.skipped }));
      return sentences.join(' ');
    }

    function noticeLimit(result){
      if(result && result.limit) showToast(limitMessage(result.limit));
    }

    function takeFiles(files, source){
      if(!files.length) return;
      batch = null;
      const result = builder.addFiles(files, source);
      noticeLimit(result);
      armBatch(result);
    }

    function libraryImages(images){
      return (Array.isArray(images) ? images : []).map(image => ({
        id: image && image.id,
        url: image && image.url,
        name: image && image.name,
        previewUrl: image && image.preview_url
      }));
    }

    function openLibrary(){
      if(typeof window.openLibraryPicker !== 'function'){
        builder.track('error_shown', { where: 'library', code: 'library_unavailable' });
        showToast(t('library_unavailable'));
        return;
      }
      window.openLibraryPicker(images => {
        batch = null;
        const result = builder.addLibrary(libraryImages(images));
        noticeLimit(result);
        armBatch(result);
      });
    }

    function cleanLibraryParams(params){
      LIBRARY_PARAMS.forEach(key => params.delete(key));
      const search = params.toString();
      try {
        window.history.replaceState(window.history.state, '', window.location.pathname + (search ? '?' + search : '') + window.location.hash);
        return true;
      } catch(e){
        return false;
      }
    }

    function readLibraryHandoff(){
      if(!el.library) return;
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch(e){
        return;
      }
      if(params.get('from') !== 'library' || !params.get('image')) return;
      batch = null;
      const result = builder.addLibrary([{
        id: params.get('image_id'),
        url: params.get('image'),
        name: params.get('name'),
        previewUrl: params.get('preview')
      }]);
      cleanLibraryParams(params);
      if(result.added.length && el.libraryNote){
        el.libraryNote.hidden = false;
        if(typeof el.libraryNote.scrollIntoView === 'function') el.libraryNote.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      }
      noticeLimit(result);
      armBatch(result);
    }

    function typeLabel(d){
      if(d.problem && strings['type_' + d.problem]) return t('type_' + d.problem);
      if(d.problem === 'unsupported' && !Logic.fileProblem({ type: d.type, name: d.name })) return '';
      return fileExtension(d.name);
    }

    function sentText(d){
      if(d.status !== STATUS.uploading || d.source !== 'file' || !(d.bytes > 0) || d.progress >= 100) return '';
      const total = d.bytes / MIB;
      const done = total * d.progress / 100;
      if(total > SENT_DETAIL_MIB) return t('sent_large', { done: Math.round(done), total: Math.round(total) });
      return t('sent_small', { done: done.toFixed(1), total: Math.max(0.1, total).toFixed(1) });
    }

    function statusText(d){
      if(d.status === STATUS.uploading) return d.progress >= 100 ? t('status_preparing') : t('status_uploading', { percent: d.progress });
      if(d.status === STATUS.refused){
        if(d.problem === 'too_large') return t('status_too_large');
        const type = typeLabel(d);
        return type ? t('status_refused', { type }) : t('status_unsupported');
      }
      return t('status_' + d.status);
    }

    function unreadable(d){
      return d.problem === 'unsupported' && !Logic.fileProblem({ type: d.type, name: d.name });
    }

    function noteFor(d, lowShown){
      if(d.status === STATUS.refused){
        if(d.problem === 'too_large') return { tone: 'err', text: t('note_too_large') };
        if(unreadable(d)) return { tone: 'err', text: t('note_unreadable') };
        const type = typeLabel(d);
        return { tone: 'err', text: type ? t('note_refused', { type }) : t('note_unsupported') };
      }
      if(d.status === STATUS.failed) return { tone: 'err', text: t('note_failed') };
      if(lowShown){
        const px = ArtCards.printPixelSize({
          isSquare: builder.size.isSquare,
          orientation: d.orientation,
          dpi: Math.min(...ArtCards.PRINT_DPI_STEPS)
        });
        return { tone: 'warn', text: t('note_low', { dpi: d.dpi, width: px.width, height: px.height }) };
      }
      return null;
    }

    function createRow(d){
      const node = clone('design-row');
      if(!node) return null;
      const ref = name => node.querySelector('[data-pb-row="' + name + '"]');
      const row = {
        node,
        src: '',
        broken: false,
        frame: ref('frame'),
        thumb: ref('thumb'),
        name: ref('name'),
        status: ref('status'),
        low: ref('low'),
        cards: ref('cards'),
        cardsInput: ref('cards-input'),
        all: ref('all'),
        progress: ref('progress'),
        sent: ref('sent'),
        note: ref('note'),
        check: ref('check'),
        edit: ref('edit'),
        retry: ref('retry'),
        remove: ref('remove')
      };
      const nameId = 'pb-design-' + id + '-' + d.id;
      if(row.name) row.name.id = nameId;
      [row.check, row.edit, row.retry].forEach(button => { if(button) button.setAttribute('aria-describedby', nameId); });
      on(row.thumb, 'load', () => {
        const current = builder.design(d.id);
        if(current && !current.width) builder.setDimensions(d.id, row.thumb.naturalWidth, row.thumb.naturalHeight);
      });
      on(row.thumb, 'error', () => {
        row.broken = true;
        row.thumb.hidden = true;
      });
      on(row.frame, 'click', () => openPicture(d.id));
      on(row.check, 'click', () => api.openEditor(d.id, id));
      on(row.edit, 'click', () => api.openEditor(d.id, id));
      on(row.retry, 'click', () => builder.retry(d.id));
      on(row.remove, 'click', () => removeDesign(d.id));
      on(row.cardsInput, 'change', () => builder.setAlloc(d.id, row.cardsInput.value));
      return row;
    }

    function fillRow(row, d, context){
      const label = d.title || d.name || t('library_name');
      const refused = d.status === STATUS.refused;
      const lowShown = d.lowRes && (d.status === STATUS.unchecked || d.status === STATUS.ready);
      const shape = builder.size.isSquare ? 'square' : d.orientation;
      const quantity = builder.state.quantity;
      const showCards = !!quantity && context.liveCount > 1 && !refused;
      const showAll = !!quantity && context.liveCount === 1 && !refused;
      const note = noteFor(d, lowShown);
      row.node.classList.toggle('pb-row--err', refused || d.status === STATUS.failed);
      row.node.classList.toggle('pb-row--warn', !!lowShown);
      if(row.frame){
        SHAPES.forEach(name => row.frame.classList.toggle('pb-row__frame--' + name, name === shape));
        row.frame.disabled = d.status === STATUS.ready ? !ycPart : d.status !== STATUS.unchecked;
        row.frame.setAttribute('aria-label', t(d.status === STATUS.ready ? 'row_preview' : 'row_open', { name: label }));
      }
      if(row.thumb){
        if(d.previewUrl && row.src !== d.previewUrl){
          row.src = d.previewUrl;
          row.broken = false;
          row.thumb.src = d.previewUrl;
        }
        row.thumb.hidden = !d.previewUrl || row.broken;
      }
      setText(row.name, label);
      if(row.status){
        row.status.textContent = statusText(d);
        Object.values(STATUS).forEach(name => row.status.classList.toggle('pb-chip--' + name, name === d.status));
      }
      if(row.low) row.low.hidden = !lowShown;
      if(row.cards) row.cards.hidden = !showCards;
      if(row.cardsInput){
        if(document.activeElement !== row.cardsInput) row.cardsInput.value = String(d.alloc || 0);
        row.cardsInput.setAttribute('aria-label', t('cards_label', { name: label }));
        if(context.invalid.has(d.id)) row.cardsInput.setAttribute('aria-invalid', 'true');
        else row.cardsInput.removeAttribute('aria-invalid');
      }
      if(row.all){
        row.all.hidden = !showAll;
        row.all.textContent = showAll ? t('all_cards', { quantity: format.number(quantity) }) : '';
      }
      if(row.progress){
        row.progress.hidden = d.status !== STATUS.uploading;
        row.progress.value = d.progress;
      }
      if(row.sent){
        const sent = sentText(d);
        row.sent.hidden = !sent;
        row.sent.textContent = sent;
      }
      if(row.note){
        row.note.hidden = !note;
        row.note.textContent = note ? note.text : '';
        row.note.classList.toggle('pb-row__note--err', !!note && note.tone === 'err');
        row.note.classList.toggle('pb-row__note--warn', !!note && note.tone === 'warn');
      }
      if(row.check) row.check.hidden = d.status !== STATUS.unchecked;
      if(row.edit) row.edit.hidden = d.status !== STATUS.ready;
      if(row.retry) row.retry.hidden = d.status !== STATUS.failed;
      if(row.remove) row.remove.setAttribute('aria-label', t('remove_label', { name: label }));
    }

    function invalidIds(allocation){
      const invalid = new Set();
      if(!allocation.shown) return invalid;
      allocation.problems.forEach(problem => {
        if(problem.code === 'sum') allocation.designs.forEach(designId => invalid.add(designId));
        else if(problem.code === 'below_min' && allocation.designs[problem.index]) invalid.add(allocation.designs[problem.index]);
      });
      return invalid;
    }

    function renderRows(allocation){
      if(!el.designs) return;
      const designs = builder.state.designs;
      const ids = new Set(designs.map(d => d.id));
      rows.forEach((row, rowId) => {
        if(ids.has(rowId)) return;
        row.node.remove();
        rows.delete(rowId);
      });
      const context = { liveCount: builder.live().length, invalid: invalidIds(allocation) };
      designs.forEach((d, index) => {
        let row = rows.get(d.id);
        if(!row){
          row = createRow(d);
          if(!row) return;
          rows.set(d.id, row);
        }
        fillRow(row, d, context);
        const at = el.designs.children[index] || null;
        if(at !== row.node) el.designs.insertBefore(row.node, at);
      });
    }

    function removeDesign(designId){
      const index = builder.state.designs.findIndex(d => d.id === designId);
      builder.remove(designId);
      focusAfterRemove(index);
    }

    function focusAfterRemove(index){
      const after = builder.state.designs;
      const next = after[index] || after[index - 1];
      const row = next ? rows.get(next.id) : null;
      const target = row && row.remove ? row.remove : el.fileInput;
      if(target) target.focus();
    }

    function openPicture(designId){
      const d = builder.design(designId);
      if(!d) return;
      if(d.status === STATUS.unchecked){
        api.openEditor(designId, id);
        return;
      }
      if(d.status === STATUS.ready && ycPart && builder.showPreview(designId)) revealPreview();
    }

    function revealPreview(){
      if(typeof ycPart.scrollIntoView !== 'function') return;
      const box = ycPart.getBoundingClientRect();
      if(box.top >= 0 && box.bottom <= window.innerHeight) return;
      ycPart.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' });
    }

    function hostCard(card){
      const host = ycPart.parentElement;
      if(!host || card === ycHosted) return;
      ycHosted = card;
      host.classList.toggle('pb-yc-host--card', card);
      if(!card) window.dispatchEvent(new Event('resize'));
    }

    function placeCard(view, name){
      if(!el.ycPic) return;
      if(view.previewUrl && ycSrc !== view.previewUrl){
        ycSrc = view.previewUrl;
        el.ycPic.src = view.previewUrl;
      }
      el.ycPic.alt = t('yc_front_alt', { name });
      el.ycPic.hidden = !view.previewUrl;
      const place = view.placement;
      el.ycPic.classList.toggle('pb-yc__pic--plain', !place);
      [['--pb-yc-x', 'left'], ['--pb-yc-y', 'top'], ['--pb-yc-w', 'width'], ['--pb-yc-h', 'height']].forEach(([property, key]) => {
        if(place) el.ycPic.style.setProperty(property, (place[key] * 100) + '%');
        else el.ycPic.style.removeProperty(property);
      });
      if(place) el.ycPic.style.setProperty('--pb-yc-turn', place.rotation + 'deg');
      else el.ycPic.style.removeProperty('--pb-yc-turn');
    }

    function showBack(view, name, asking){
      const back = view.back;
      const empty = !back || back.empty;
      if(el.ycBlank) el.ycBlank.hidden = !empty;
      if(!el.ycBackImg) return;
      el.ycBackImg.alt = t('yc_back_alt', { name });
      const current = !empty && ycShown.key === back.key;
      el.ycBackImg.hidden = !current || !ycShown.image;
      if(empty || current || !asking || ycAsked === back.key) return;
      ycAsked = back.key;
      builder.previewBack().then(result => {
        const shown = builder.previewView().back;
        if(!shown || shown.key !== result.key){
          if(ycAsked === result.key) ycAsked = null;
          return;
        }
        ycShown = result;
        if(result.image) el.ycBackImg.src = result.image;
        renderPreview();
      });
    }

    function renderPreview(){
      if(!ycPart) return;
      const view = builder.previewView();
      const card = view.hasCard && view.view === VIEWS[1];
      const front = view.side === SIDES[0];
      ycPart.hidden = !view.hasCard;
      hostCard(card);
      [[el.ycPhotos, !card], [el.ycCard, card], [el.ycShowFront, front], [el.ycShowBack, !front]].forEach(([button, active]) => {
        if(button) button.setAttribute('aria-pressed', String(active));
      });
      if(el.ycView) el.ycView.hidden = !card;
      if(!view.hasCard) return;
      const name = view.name || t('library_name');
      [el.ycFront, el.ycBack].forEach(frame => {
        if(frame) SHAPES.forEach(shape => frame.classList.toggle('pb-yc__card--' + shape, shape === view.shape));
      });
      if(el.ycFront){
        el.ycFront.hidden = !front;
        el.ycFront.classList.toggle('pb-yc__card--white', !!view.fitMode && view.fitMode !== FIT_MODES[0]);
      }
      if(el.ycBack) el.ycBack.hidden = front;
      placeCard(view, name);
      showBack(view, name, card && !front);
      setText(el.ycCaption, t(view.cards != null ? 'yc_caption' : 'yc_caption_none', {
        side: t(front ? 'yc_front' : 'yc_back'),
        name,
        cards: view.cards != null ? format.number(view.cards) : ''
      }));
    }

    function renderAlloc(allocation){
      if(!el.alloc) return;
      el.alloc.hidden = !allocation.shown;
      if(!allocation.shown) return;
      const ok = !allocation.problems.length;
      const sum = allocation.problems.find(p => p.code === 'sum');
      const below = allocation.problems.find(p => p.code === 'below_min' || p.code === 'below_single_min');
      let text = t('alloc_ok', { quantity: format.number(allocation.quantity) });
      if(sum) text = t('alloc_sum', { total: format.number(sum.actual), quantity: format.number(sum.expected) });
      else if(below) text = t('alloc_min', { min: below.min });
      el.alloc.classList.toggle('pb-alloc--ok', ok);
      el.alloc.classList.toggle('pb-alloc--bad', !ok);
      setText(el.allocText, text);
    }

    function renderQuantity(){
      const quantity = builder.state.quantity;
      tileButtons.forEach((node, value) => node.setAttribute('aria-pressed', String(value === quantity)));
      if(el.more) el.more.value = quantity && !tileButtons.has(quantity) ? String(quantity) : '';
      if(!el.rule) return;
      let text = t('rule_none', { max: rules.maxDesigns, min: rules.minPerDesignMulti });
      if(quantity){
        const allowed = Logic.maxDesigns(quantity, rules);
        text = allowed === 1
          ? t('rule_one', { quantity: format.number(quantity), need: format.number(Logic.designLimit(quantity, 2, rules).need) })
          : t('rule_many', { quantity: format.number(quantity), allowed, min: rules.minPerDesignMulti });
      }
      el.rule.textContent = text;
    }

    function renderSteps(){
      [1, 2].forEach(step => {
        const done = builder.stepDone(step);
        const num = find('step-' + step);
        const mark = find('step-' + step + '-done');
        if(num) num.classList.toggle('pb-step__num--done', done);
        if(mark) mark.hidden = !done;
      });
    }

    function renderCount(){
      const limit = builder.limits();
      setText(el.count, t(limit.max === 1 ? 'count_one' : 'count_many', { count: limit.count, max: limit.max }));
    }

    function pressed(nodes, attribute, value){
      nodes.forEach(node => node.setAttribute('aria-pressed', String(node.getAttribute(attribute) === value)));
    }

    function modeNote(view){
      if(view.fitMode === 'fit') return view.stretchShown ? t('ed_mode_fit', { percent: view.stretch }) : t('ed_mode_fit_slight');
      return t('ed_mode_' + view.fitMode);
    }

    function placePicture(view){
      if(!el.edPic) return;
      if(view.previewUrl && edPicSrc !== view.previewUrl){
        edPicSrc = view.previewUrl;
        el.edPic.src = view.previewUrl;
      }
      const place = view.placement;
      el.edPic.hidden = !place || !view.previewUrl;
      if(!place) return;
      [['--pb-ed-x', place.left], ['--pb-ed-y', place.top], ['--pb-ed-w', place.width], ['--pb-ed-h', place.height]]
        .forEach(([name, value]) => el.edPic.style.setProperty(name, (value * 100) + '%'));
      el.edPic.style.setProperty('--pb-ed-turn', place.rotation + 'deg');
    }

    function fillEditor(view){
      const front = view.tab === TABS[0];
      const shape = builder.size.isSquare ? 'square' : view.orientation;
      setText(el.edHeading, t('ed_heading', { number: view.number, count: view.count }));
      setText(el.edFile, t('ed_file', { name: view.name || t('library_name') }));
      [[el.edTabFront, front], [el.edTabBack, !front]].forEach(([tab, current]) => {
        if(!tab) return;
        if(current) tab.setAttribute('aria-current', 'step');
        else tab.removeAttribute('aria-current');
      });
      if(el.edFront) el.edFront.hidden = !front;
      if(el.edBack) el.edBack.hidden = front;
      if(el.edCard) SHAPES.forEach(name => el.edCard.classList.toggle('pb-ed__card--' + name, name === shape));
      if(el.edFrame){
        el.edFrame.classList.toggle('pb-ed__frame--drag', view.canMove);
        el.edFrame.classList.toggle('pb-ed__frame--white', view.fitMode !== 'crop');
      }
      placePicture(view);
      setText(el.edHint, t('ed_hint_' + view.fitMode));
      if(el.edTitle && document.activeElement !== el.edTitle) el.edTitle.value = view.title;
      pressed(fitButtons, 'data-pb-fit', view.fitMode);
      setText(el.edModeNote, view.tooLarge ? t('ed_too_large') : modeNote(view));
      if(el.edModeNote) el.edModeNote.classList.toggle('pb-ed__note--err', !!view.tooLarge);
      if(el.edTabBack) el.edTabBack.disabled = !!view.tooLarge;
      if(el.edOrient) el.edOrient.hidden = !view.showOrientation;
      pressed(orientButtons, 'data-pb-orient', view.orientation);
      if(el.edZoom){
        el.edZoom.value = String(view.zoom);
        el.edZoom.disabled = !view.canMove;
      }
      [el.edZoomOut, el.edZoomIn].concat(moveButtons).forEach(button => { if(button) button.disabled = !view.canMove; });
      setText(el.edMoveNote, t(view.fitMode === 'crop' ? 'ed_move_crop' : 'ed_move_off'));
      if(el.edDpi){
        el.edDpi.hidden = !view.quality;
        el.edDpi.classList.toggle('pb-ed__dpi--low', view.quality === 'low');
        el.edDpi.classList.toggle('pb-ed__dpi--ok', !!view.quality && view.quality !== 'low');
      }
      setText(el.edDpiTitle, view.quality ? t('ed_dpi_' + view.quality, { dpi: view.dpi }) : '');
      setText(el.edDpiText, view.quality ? t(view.quality === 'low' ? 'ed_dpi_low_text' : 'ed_dpi_ok_text') : '');
      if(el.edNext){
        el.edNext.hidden = !front;
        el.edNext.disabled = !!view.tooLarge;
      }
      if(el.edBackStep) el.edBackStep.hidden = front;
      if(el.edConfirm){
        el.edConfirm.hidden = front;
        el.edConfirm.disabled = !view.canConfirm;
        el.edConfirm.textContent = t(view.next ? 'ed_confirm_next' : 'ed_confirm');
      }
    }

    function backContext(){
      const now = () => builder.backState() || {};
      return {
        get design(){ return now().design || null; },
        get sharedBack(){ return now().sharedBack || null; },
        get sharedLogo(){ return now().sharedLogo || null; },
        get size(){ return builder.size; },
        get orientation(){ return now().orientation; },
        get isFirst(){ return !!now().isFirst; },
        get firstTitle(){ return now().firstTitle || ''; },
        update: patch => builder.updateBack(patch)
      };
    }

    function syncBack(view){
      const wanted = view && backEditor && el.edBack ? view.id : null;
      if(backFor !== wanted){
        if(backHandle) backHandle.unmount();
        backHandle = null;
        backFor = wanted;
        if(wanted) backHandle = backEditor.mount(el.edBack, backContext()) || null;
        return;
      }
      if(backHandle && view.tab === TABS[1] && typeof backHandle.render === 'function') backHandle.render();
    }

    function measureBack(d){
      return backHandle ? backHandle.measureTextPct(d) : null;
    }

    function renderEditor(){
      if(!el.editor) return;
      const view = builder.editor();
      if(!view){
        syncBack(null);
        edShown = null;
        closeDialog(el.editor);
        return;
      }
      fillEditor(view);
      syncBack(view);
      edSession = { id: view.id, index: builder.state.designs.findIndex(d => d.id === view.id) };
      if(!isOpen(el.editor)){
        showDialog(el.editor, edFrom);
        edFrom = null;
      }
      if(edShown !== view.id){
        edShown = view.id;
        if(el.edPanel) el.edPanel.scrollTop = 0;
        el.editor.focus({ preventScroll: true });
      }
    }

    function focusAfterEditor(){
      const session = edSession;
      edSession = null;
      if(!session) return;
      const row = rows.get(session.id);
      const target = row ? [row.edit, row.check].find(button => button && !button.hidden) : null;
      if(row && !target){
        restoreFocus(el.editor);
        return;
      }
      openers.delete(el.editor);
      if(target) target.focus();
      else focusAfterRemove(session.index);
    }

    function onEditorClose(){
      if(isOpen(el.editor)) return;
      if(builder.editor()) closeByHand();
      edShown = null;
      focusAfterEditor();
    }

    function onEditorKey(event){
      if(event.key === 'Escape'){
        event.preventDefault();
        event.stopPropagation();
        closeByHand();
        return;
      }
      if(event.key === 'Tab') trapFocus(event, el.editor);
    }

    function openEditorFor(designId){
      if(!el.editor) return false;
      edFrom = document.activeElement;
      if(builder.openEditor(designId)) return true;
      edFrom = null;
      return false;
    }

    function showTab(tab, focusTarget){
      builder.setEditorTab(tab);
      if(focusTarget) focusTarget.focus();
    }

    function zoomStep(direction){
      const view = builder.editor();
      if(view) builder.setZoom(view.zoom + direction * steps.zoomButton);
    }

    function moveStep(direction, step){
      const vector = MOVES[direction];
      if(vector) builder.moveBy(vector[0] * step, vector[1] * step);
    }

    function startDrag(view){
      const point = pointers.values().next().value;
      const box = el.edFrame.getBoundingClientRect();
      drag = point && view && view.crop && box.width && box.height
        ? { x: point.x, y: point.y, crop: view.crop, width: box.width, height: box.height }
        : null;
    }

    function startPinch(view){
      const [a, b] = Array.from(pointers.values());
      pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom: view.zoom };
      drag = null;
    }

    function onFrameDown(event){
      const view = builder.editor();
      if(!view || !view.canMove || pointers.size >= 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      el.edFrame.focus({ preventScroll: true });
      if(typeof el.edFrame.setPointerCapture === 'function') el.edFrame.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if(pointers.size === 2) startPinch(view);
      else startDrag(view);
    }

    function onFrameMove(event){
      if(!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if(pinch && pointers.size === 2){
        const [a, b] = Array.from(pointers.values());
        builder.setZoom(pinch.zoom * Math.hypot(a.x - b.x, a.y - b.y) / pinch.distance);
        return;
      }
      if(drag) builder.setCenter(Logic.panCenter(drag.crop, (event.clientX - drag.x) / drag.width, (event.clientY - drag.y) / drag.height));
    }

    function onFrameUp(event){
      if(!pointers.delete(event.pointerId)) return;
      pinch = null;
      if(pointers.size) startDrag(builder.editor());
      else drag = null;
    }

    function onFrameKey(event){
      const direction = KEYS[event.key];
      if(!direction) return;
      event.preventDefault();
      moveStep(direction, steps.keyStep);
    }

    function bindEditor(){
      if(!el.editor) return;
      on(el.editor, 'keydown', onEditorKey);
      on(el.editor, 'cancel', event => event.preventDefault());
      on(el.editor, 'close', onEditorClose);
      on(el.edClose, 'click', closeByHand);
      on(el.edCancel, 'click', closeByHand);
      on(el.edTabFront, 'click', () => builder.setEditorTab(TABS[0]));
      on(el.edTabBack, 'click', () => builder.setEditorTab(TABS[1]));
      on(el.edNext, 'click', () => showTab(TABS[1], el.edTabBack));
      on(el.edBackStep, 'click', () => showTab(TABS[0], el.edTabFront));
      on(el.edConfirm, 'click', () => builder.confirmEditor(measureBack));
      on(el.edTitle, 'input', () => builder.setTitle(el.edTitle.value));
      fitButtons.forEach(button => on(button, 'click', () => builder.setFitMode(button.getAttribute('data-pb-fit'))));
      orientButtons.forEach(button => on(button, 'click', () => builder.setOrientation(button.getAttribute('data-pb-orient'))));
      moveButtons.forEach(button => on(button, 'click', () => moveStep(button.getAttribute('data-pb-move'), steps.moveStep)));
      on(el.edZoom, 'input', () => builder.setZoom(el.edZoom.value));
      on(el.edZoomOut, 'click', () => zoomStep(-1));
      on(el.edZoomIn, 'click', () => zoomStep(1));
      on(el.edRotate, 'click', () => builder.rotate());
      on(el.edReset, 'click', () => builder.resetCrop());
      on(el.edFrame, 'pointerdown', onFrameDown);
      on(el.edFrame, 'pointermove', onFrameMove);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => on(el.edFrame, type, onFrameUp));
      on(el.edFrame, 'keydown', onFrameKey);
      on(el.edPic, 'load', () => {
        const view = builder.editor();
        const d = view ? builder.design(view.id) : null;
        if(d && !d.width && d.previewUrl === edPicSrc) builder.setDimensions(d.id, el.edPic.naturalWidth, el.edPic.naturalHeight);
      });
    }

    function addRow(){
      const node = clone('add-row');
      if(!node) return null;
      return { node, text: node.querySelector('[data-pb-add="text"]'), state: node.querySelector('[data-pb-add="state"]') };
    }

    function renderAddRows(list){
      if(!el.addingList) return;
      if(addRows.length !== list.length){
        addRows.length = 0;
        list.forEach(() => {
          const row = addRow();
          if(row) addRows.push(row);
        });
        el.addingList.replaceChildren(...addRows.map(row => row.node));
      }
      list.forEach((item, index) => {
        const row = addRows[index];
        if(!row) return;
        ['done', 'now', 'wait'].forEach(name => row.node.classList.toggle('pb-adding__row--' + name, name === item.state));
        setText(row.text, item.text);
        setText(row.state, item.sr);
      });
    }

    function sizeSpacer(){
      if(el.bar && el.barSpace) el.barSpace.style.setProperty('--pb-bar-h', el.bar.offsetHeight + 'px');
    }

    function renderBar(text){
      if(!el.bar) return;
      el.bar.hidden = isOpen(el.editor);
      setText(el.barTotal, text.total);
      setText(el.barSub, text.barSub);
      setText(el.barGo, text.barLabel);
      sizeSpacer();
    }

    function focusOutcome(status, refused){
      if(status === addShown) return;
      addShown = status;
      const target = refused ? el.addCancel : el.addRetry;
      if(status === ADD.failed && target) target.focus();
      else if(status === ADD.done && el.addDoneTitle) el.addDoneTitle.focus();
    }

    function renderSummary(){
      const view = builder.summary();
      const text = summaryText(view, strings, format);
      const status = view.add.status;
      const busy = status === ADD.adding;
      const refused = !!(view.add.failed && view.add.failed.refused);
      lockedParts.concat(el.packs ? [el.packs] : []).forEach(node => node.toggleAttribute('inert', busy));
      packButtons.forEach(button => button.setAttribute('aria-pressed', String(button.getAttribute('data-pb-pack') === view.packaging)));
      setText(el.sleevesPrice, text.sleeves);
      setText(el.sumSize, text.size);
      setText(el.sumCards, text.cards);
      setText(el.sumDesigns, text.designs);
      setText(el.sumPack, text.packaging);
      if(el.rush){
        el.rush.checked = view.rush;
        el.rush.disabled = busy;
      }
      setText(el.sumTotal, text.total);
      setText(el.sumEach, text.each);
      if(el.sumEach) el.sumEach.hidden = !text.each;
      if(el.summary) el.summary.setAttribute('aria-busy', String(busy));
      if(el.addCart){
        el.addCart.hidden = status !== ADD.idle;
        el.addCart.disabled = !!view.reason;
      }
      if(el.why) el.why.hidden = status !== ADD.idle || !text.why;
      setText(el.whyText, text.why);
      if(el.save){
        el.save.disabled = !builder.canSave();
        const label = t(builder.state.save.status === SAVE.saving ? 'save_saving' : 'save_label');
        if(el.save.textContent !== label) el.save.textContent = label;
      }
      if(el.adding) el.adding.hidden = !busy;
      renderAddRows(text.rows);
      if(el.addFailed) el.addFailed.hidden = status !== ADD.failed;
      setText(el.addFailedTitle, text.failedTitle);
      setText(el.addFailedText, text.failedText);
      if(el.addRetry) el.addRetry.hidden = refused;
      if(el.addDone) el.addDone.hidden = status !== ADD.done;
      setText(el.addDoneText, text.doneText);
      renderBar(text);
      focusOutcome(status, refused);
    }

    function focusHeading(){
      if(el.sumTitle) el.sumTitle.focus({ preventScroll: true });
    }

    function focusStart(){
      if(!el.stepTitle) return;
      if(typeof el.stepTitle.scrollIntoView === 'function') el.stepTitle.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      el.stepTitle.focus({ preventScroll: true });
    }

    function goToSummary(){
      if(!builder.reasons().length && builder.state.add.status === ADD.idle) builder.addToCart();
      if(el.summary && typeof el.summary.scrollIntoView === 'function') el.summary.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    }

    function bindSummary(){
      packButtons.forEach(button => on(button, 'click', () => {
        builder.setPackaging(ArtCards.PACKAGING[button.getAttribute('data-pb-pack') === 'sleeves' ? 1 : 0]);
      }));
      on(el.rush, 'change', () => {
        if(!builder.setRush(el.rush.checked)) el.rush.checked = builder.state.rush;
      });
      on(el.addCart, 'click', () => { if(builder.addToCart()) focusHeading(); });
      on(el.addRetry, 'click', () => { if(builder.retryAdd()) focusHeading(); });
      on(el.addCancel, 'click', () => { if(builder.cancelAdd() && el.addCart) el.addCart.focus(); });
      on(el.addAgain, 'click', () => { if(builder.startOver()) focusStart(); });
      on(el.barGo, 'click', goToSummary);
      if(el.cartLink && typeof summaryConfig.cartUrl === 'string' && summaryConfig.cartUrl) el.cartLink.setAttribute('href', summaryConfig.cartUrl);
      if(el.bar && typeof ResizeObserver === 'function') new ResizeObserver(sizeSpacer).observe(el.bar);
    }

    function noticeSave(result){
      if(result === 'saved') showToast(t('save_done'), typeof accountConfig.myDesignsUrl === 'string' ? accountConfig.myDesignsUrl : '');
      else if(result === 'failed') showToast(t('save_failed'));
    }

    function saveForLater(){
      builder.saveDesigns().then(result => {
        if(result === 'sign_in') showDialog(el.signin, el.save);
        else noticeSave(result);
      });
    }

    function bindAccount(){
      on(el.save, 'click', saveForLater);
      on(el.signinOpen, 'click', () => showDialog(el.signin, el.signinOpen));
      on(el.signinClose, 'click', () => closeDialog(el.signin));
      on(el.signin, 'close', () => restoreFocus(el.signin));
      signinLinks.forEach(link => on(link, 'click', event => {
        if(uploading()){
          event.preventDefault();
          return;
        }
        stashPack();
      }));
    }

    function bindPreview(){
      on(el.ycPhotos, 'click', () => builder.setPreviewView(VIEWS[0]));
      on(el.ycCard, 'click', () => builder.setPreviewView(VIEWS[1]));
      on(el.ycShowFront, 'click', () => builder.setPreviewSide(SIDES[0]));
      on(el.ycShowBack, 'click', () => builder.setPreviewSide(SIDES[1]));
    }

    function watchView(){
      const target = parts.find(node => node.getAttribute('data-pb-part') === 'quantity') || parts.find(node => node !== ycPart);
      if(!target) return;
      if(typeof IntersectionObserver !== 'function'){
        builder.track('builder_view', {});
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if(!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        builder.track('builder_view', {});
      });
      observer.observe(target);
    }

    function render(){
      const allocation = builder.allocation();
      buildOptions();
      renderQuantity();
      renderSteps();
      renderCount();
      renderRows(allocation);
      renderAlloc(allocation);
      renderKeep();
      renderEditor();
      renderSummary();
      renderPreview();
      renderWait();
      renderLeaving();
      openBatch();
    }

    function bind(){
      sizeLinks.forEach(link => link.addEventListener('click', event => {
        if(link.getAttribute('aria-current') === 'page'){
          event.preventDefault();
          return;
        }
        if(builder.live().length){
          event.preventDefault();
          openSwitchDialog(link);
          return;
        }
        rememberSwitch();
        leavePack();
      }));
      on(el.switchGo, 'click', event => {
        if(uploading()){
          event.preventDefault();
          return;
        }
        rememberSwitch();
        leavePack();
      });
      on(el.switchStay, 'click', () => closeDialog(el.switchDialog));
      on(el.switchDialog, 'close', () => restoreFocus(el.switchDialog));
      on(window, 'pageshow', event => { if(event.persisted === true) packOff = false; });
      document.addEventListener('close', () => setTimeout(openBatch, 0), true);
      on(el.more, 'change', () => {
        const quantity = parseInt(el.more.value, 10);
        if(quantity > 0) chooseQuantity(quantity, el.more);
      });
      on(el.keepCancel, 'click', () => closeDialog(el.keepDialog));
      on(el.keepApply, 'click', () => { if(builder.applyKeep()) closeDialog(el.keepDialog); });
      on(el.keepDialog, 'close', () => {
        builder.cancelKeep();
        restoreFocus(el.keepDialog);
      });
      on(el.toastClose, 'click', hideToast);
      on(el.fileInput, 'change', () => {
        const files = Array.from(el.fileInput.files || []);
        el.fileInput.value = '';
        takeFiles(files);
      });
      on(el.drop, 'dragover', event => {
        event.preventDefault();
        el.drop.classList.add('pb-drop--over');
      });
      on(el.drop, 'dragleave', event => {
        if(!el.drop.contains(event.relatedTarget)) el.drop.classList.remove('pb-drop--over');
      });
      on(el.drop, 'drop', event => {
        event.preventDefault();
        el.drop.classList.remove('pb-drop--over');
        takeFiles(Array.from((event.dataTransfer && event.dataTransfer.files) || []), 'drag');
      });
      on(el.library, 'click', openLibrary);
      on(el.libraryNoteOk, 'click', () => { el.libraryNote.hidden = true; });
      bindEditor();
      bindSummary();
      bindAccount();
      bindPreview();
    }

    buildTiles();
    buildOptions();
    moveToast();
    moveToBody(el.bar, 'data-pb-bar-for');
    bind();
    builder.subscribe(render);
    const stash = readStash();
    const claimed = claimGuestRefs();
    const switched = restoreSwitch(!!stash);
    if(stash) returnFromSignIn(stash, claimed);
    else if(!switched) restoreKept();
    builder.subscribe(rememberPack);
    readLibraryHandoff();
    render();
    watchView();
    const instance = { id, builder, render, showToast, openEditor: openEditorFor };
    instances.set(id, instance);
    return instance;
  }

  function mountAll(){
    if(typeof document === 'undefined') return;
    const ids = new Set(Array.from(document.querySelectorAll('[data-pb-id]')).map(node => node.getAttribute('data-pb-id')));
    ids.forEach(id => {
      try {
        mount(id);
      } catch(error){
        document.dispatchEvent(new CustomEvent('print-builder:error', { detail: { id, error } }));
      }
    });
  }

  const api = {
    STATUS,
    SWITCH_KEY,
    ADD,
    REASON_ORDER,
    instances,
    fill,
    fileExtension,
    findVariant,
    quantities,
    reasonOf,
    reasonText,
    summaryText,
    sendEvent,
    createBuilder,
    serverUploadStub,
    printSize,
    libraryId,
    openEditor,
    mount,
    mountAll
  };

  if(typeof document !== 'undefined' && typeof window !== 'undefined'){
    if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
    else mountAll();
    document.addEventListener('shopify:section:load', mountAll);
  }

  return api;
});
